import { useState,useEffect,useRef,useMemo } from "react";
import { Truck, Package, MapPin, CheckCircle, XCircle, Clock, AlertTriangle, Search, ChevronDown, ChevronUp, Plus, X, Navigation, Warehouse, Users, ArrowRight, Circle, RefreshCw, Eye, Phone, Star, Zap, Globe, Route, ClipboardList ,Edit,AlertCircle} from "lucide-react";
import { Card } from "../../Components/Card";
import { Button } from "../../Components/Button";
import { Badge } from "../../Components/Badge";
import { Input } from "../../Components/Input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../Components/Tabs";
import { ScrollArea } from "../../Components/ScrollArea";
import { toast } from "sonner";
import Pagination, { usePagination } from "../../Components/Pagination";
import { decryptData } from "../../../utils/storageCrypto";

const formatDuration = (mins) => {
  if (!mins) return "N/A";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDate = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatEstimatedTime = (time) => {
  if (!time) return "TBD";
  if (typeof time === "object" && time.minMinutes !== undefined && time.maxMinutes !== undefined) {
    return `${time.minMinutes}-${time.maxMinutes} mins`;
  }
  return time;
};

// Calculate distance in km between two coordinate points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  return parseFloat((R * c).toFixed(1));
};

// Add minutes to a datetime-local string
const addMinutesToDateTimeLocal = (dateTimeStr, minutes) => {
  if (!dateTimeStr) return "";
  const date = new Date(dateTimeStr);
  date.setMinutes(date.getMinutes() + minutes);
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - (offset * 60 * 1000));
  return localDate.toISOString().slice(0, 16);
};

// ─── Config helpers ──────────────────────────────────────────────────────────
const tripStatusConfig = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-700", dot: "bg-gray-400" },
  assigned: { label: "Assigned", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  dispatched: { label: "Dispatched", color: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
  in_transit: { label: "In Transit", color: "bg-yellow-100 text-yellow-800", dot: "bg-yellow-500" },
  at_hub: { label: "At Hub", color: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  out_for_delivery: { label: "Out for Delivery", color: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-700", dot: "bg-green-500" },
  delayed: { label: "Delayed", color: "bg-red-100 text-red-700", dot: "bg-red-500" },
  failed: { label: "Failed", color: "bg-red-200 text-red-900", dot: "bg-red-700" },
};

const legStatusIcon = {
  pending: { icon: Circle, color: "text-gray-400" },
  in_transit: { icon: Navigation, color: "text-yellow-500" },
  completed: { icon: CheckCircle, color: "text-green-500" },
  delayed: { icon: AlertTriangle, color: "text-red-500" },
};

const partnerTypeConfig = {
  "Brickbucket": { label: "Own Fleet", color: "bg-green-100 text-green-700", icon: Truck },
  "3 PL": { label: "3PL", color: "bg-blue-100 text-blue-700", icon: Globe },
  "On Demand": { label: "On Demand", color: "bg-purple-100 text-purple-700", icon: Zap },
  "Supplier": { label: "Supplier", color: "bg-orange-100 text-orange-700", icon: Route },
};

const vehicleTypeIcon = {
  van: "🚐", truck: "🚛", semi: "🚚", bike: "🏍️", container: "🚢",
};

const vehicleStatusConfig = {
  available: { label: "Available", color: "bg-green-100 text-green-700" },
  in_use: { label: "In Use", color: "bg-blue-100 text-blue-700" },
  maintenance: { label: "Maintenance", color: "bg-yellow-100 text-yellow-800" },
  offline: { label: "Offline", color: "bg-gray-100 text-gray-600" },
};

// ─── Tracking Timeline ────────────────────────────────────────────────────────
/* function TrackingTimeline({ events }) {
  return (
    <div className="relative ml-2 space-y-0">
      {events?.map((ev, i) => {
        const isLast = i === events?.length - 1;
        return (
          <div key={i} className="flex gap-3 relative">
            {!isLast && (
              <div className={`absolute left-[7px] top-5 w-0.5 h-full ${ev?.status === "completed" ? "bg-green-300" : "bg-gray-200"}`} />
            )}
            <div className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 border-2 z-10 ${
              ev?.status === "completed"
                ? "bg-green-500 border-green-500"
                : ev?.status === "current"
                ? "bg-yellow-400 border-yellow-400 ring-2 ring-yellow-200"
                : "bg-white border-gray-300"
            }`} />
            <div className={`pb-4 ${isLast ? "" : ""}`}>
              <p className={`text-sm font-medium ${ev?.status === "pending" ? "text-muted-foreground" : ""}`}>
                {ev?.event}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <MapPin className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{ev?.location}</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground">{ev?.time}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
} */

// ─── Tracking Timeline (new) ────────────────────────────────────────────────────────
/* function TrackingTimeline({ events = [] }) {
  const safeEvents = events || [];
  return (
    <div className="relative ml-2 space-y-0">
      {safeEvents.map((ev, i) => {
        const isLast = i === safeEvents.length - 1;
        return (
          <div key={i} className="flex gap-3 relative">
            {!isLast && (
              <div className={`absolute left-[7px] top-5 w-0.5 h-full ${ev?.status === "completed" ? "bg-green-300" : "bg-gray-200"}`} />
            )}
            <div className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 border-2 z-10 ${
              ev?.status === "completed"
                ? "bg-green-500 border-green-500"
                : ev?.status === "current"
                ? "bg-yellow-400 border-yellow-400 ring-2 ring-yellow-200"
                : "bg-white border-gray-300"
            }`} />
            <div className="pb-4">
              <p className={`text-sm font-medium ${ev?.status === "pending" ? "text-muted-foreground" : ""}`}>
                {ev?.event}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <MapPin className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{ev?.location}</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground">{ev?.time}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}   */

//latest
// Modify the TrackingTimeline component to accept an action callback[cite: 1]
function TrackingTimeline({ events = [], onUpdateEvent }) {
  return (
    <div className="relative ml-2 space-y-0">
      {events.map((ev, i) => {
        const isLast = i === events.length - 1;
        return (
          <div key={i} className="flex gap-3 relative">
            {!isLast && <div className={`absolute left-[7px] top-5 w-0.5 h-full ${ev?.status === "completed" ? "bg-green-300" : "bg-gray-200"}`} />}
            <div className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 border-2 z-10 ${ev?.status === "completed" ? "bg-green-500 border-green-500" : "bg-white border-gray-300"}`} />
            <div className="pb-4 w-full flex justify-between items-center">
              <div>
                <p className="text-sm font-medium">{ev?.event}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <MapPin className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{ev?.location}</span>
                  {ev?.time && <span className="text-xs text-muted-foreground ml-1">· {ev.time}</span>}
                </div>
                {ev?.isDelayed && (
                  <div className="mt-1 flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded w-max">
                    <AlertTriangle className="w-3 h-3" />
                    Delayed by {ev.delayMinutes} mins
                  </div>
                )}
              </div>
              {ev.isActionable && (
                <Button size="sm" onClick={() => onUpdateEvent(ev.legId, ev.id, 'COMPLETED')}>
                  Mark Complete
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Trip Detail Panel (old) ────────────────────────────────────────────────────
/* function TripDetailPanel({ trip, partners, vehicles, onClose, onStatusChange }) {
  const [expandedLeg, setExpandedLeg] = useState(trip?.route_legs[0]?.id ?? null);
  const partner = partners.find((p) => p.id === trip.partnerId);
  const vehicle = vehicles.find((v) => v.id === trip.vehicleId);
  const canDispatch = trip.status === "assigned";
  const canMarkDelivered = trip.status === "out_for_delivery";
  const canAssign = trip.status === "draft";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-2 sm:p-4 overflow-hidden">
      <div className="w-full max-w-4xl h-[90vh] max-h-[850px] flex flex-col overflow-hidden rounded-xl sm:rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0 bg-background">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-semibold text-base">{trip.id}</h3>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${tripStatusConfig[trip.status].color}`}>
                {tripStatusConfig[trip.status].label}
              </span>
              <Badge variant="outline" className="text-xs">
                {trip.type === "hub_and_spoke" ? "Hub & Spoke" : "Direct"}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{trip.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Orders", value: trip?.orders?.length?.toString() },
              { label: "Total Weight", value: trip?.totalWeight },
              { label: "Total Value", value: `₹${trip?.totalValue?.toLocaleString()}` },
              { label: "Est. Delivery", value: trip?.estimatedDelivery },
            ].map((s) => (
              <Card key={s.label} className="p-3 text-center">
                <p className="text-lg font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </Card>
            ))}
          </div>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Origin</p>
                <p className="text-sm font-medium">{trip.origin}</p>
              </div>
              {trip.route_legs.map((leg) => {
                const LegIcon = legStatusIcon[leg.status]?.icon || Circle;
                const isExpanded = expandedLeg === leg.id;
                
                // Convert backend 'stops' schema into UI timeline events
                const trackingEvents = (leg.stops || []).map(stop => ({
                  time: stop.createdAt ? formatDate(stop.createdAt) : leg.arrivalPlanned,
                  location: stop.location?.address || "Unknown Location",
                  event: stop.stopType === "PICKUP" ? "Arrived for Pickup" : "Arrived for Dropoff",
                  status: stop.status === "COMPLETED" ? "completed" : stop.status === "PENDING" ? "pending" : "current"
                }));

                return (
                  <Card key={leg.id} className="overflow-hidden">
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t bg-muted/20">
                        <div className="pt-4">
                          <TrackingTimeline events={trackingEvents} />
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
              <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 text-right">
                <p className="text-xs text-muted-foreground">Destination</p>
                <p className="text-sm font-medium">{trip.destination}</p>
              </div>
            </div>
          </Card>

          <div>
            <p className="text-sm font-medium mb-2">Orders in this trip</p>
            <div className="space-y-2">
              {trip?.orders?.map((order) => (
                <div key={order?.orderId} className="flex items-center gap-4 p-3 border rounded-lg text-sm bg-card">
                  <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-mono font-medium">{order?.orderNumber}</span>
                  <span className="text-muted-foreground flex-1 truncate">{order?.customer_name}</span>
                  <span className="text-muted-foreground text-xs sm:text-sm">{order?.items} items · {order?.weight}</span>
                  <div className="flex items-center gap-1 text-muted-foreground text-xs sm:text-sm">
                    <MapPin className="w-3 h-3" />
                    <span>{order?.destination}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Delivery Partner</p>
              {partner ? (
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${partnerTypeConfig[partner?.type]?.color}`}>
                      {partnerTypeConfig[partner?.type]?.label}
                    </span>
                  </div>
                  <p className="font-medium mt-1">{partner?.name}</p>
                  <p className="text-sm text-muted-foreground">{partner?.contactName}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <span className="text-xs">{partner?.rating} · {partner?.onTimeRate}% on-time</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not assigned</p>
              )}
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Vehicle / Driver</p>
              {vehicle ? (
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{vehicleTypeIcon[vehicle?.type]}</span>
                    <span className="font-medium font-mono text-sm">{vehicle?.registration}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{vehicle?.driver}</p>
                  <p className="text-xs text-muted-foreground">{vehicle?.driverPhone}</p>
                  <p className="text-xs text-muted-foreground mt-1">📍 {vehicle?.currentLocation}</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No vehicle assigned</p>
              )}
            </Card>
          </div>

          {trip.route_legs.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Trip Route Legs & Tracking</p>
              <div className="space-y-3">
                {trip.route_legs.map((leg) => {
                  const LegIcon = legStatusIcon[leg.status].icon;
                  const isExpanded = expandedLeg === leg.id;
                  return (
                    <Card key={leg.id} className="overflow-hidden">
                      <button 
                        className="w-full flex items-center gap-3 p-4 hover:bg-accent/30 transition-colors text-left" 
                        onClick={() => setExpandedLeg(isExpanded ? null : leg.id)}
                      >
                        <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                          leg.status === "completed" ? "bg-green-100" :
                          leg.status === "in_transit" ? "bg-yellow-100" :
                          leg.status === "delayed" ? "bg-red-100" : "bg-gray-100"
                        }`}>
                          <LegIcon className={`w-4 h-4 ${legStatusIcon[leg.status].color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Leg {leg.sequence} —{" "}
                              {leg.type === "supplier_to_hub" ? "Supplier → Hub" :
                              leg.type === "hub_to_hub" ? "Hub → Hub" :
                              leg.type === "hub_to_delivery" ? "Hub → Delivery" : "Direct"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-sm">
                            <span className="font-medium truncate">{leg.from}</span>
                            <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium truncate">{leg.to}</span>
                          </div>
                          {leg.carrier && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {leg.carrier}{leg.vehicle ? ` · ${leg.vehicle}` : ""}{leg.driver ? ` · ${leg.driver}` : ""}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0 text-xs text-muted-foreground">
                          <p>ETA: {leg?.arrivalPlanned?.split(" ")[0]}</p>
                          {leg.arrivalActual && <p className="text-green-600">✓ {leg?.arrivalActual?.split(" ")[0]}</p>}
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                      </button>
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t bg-muted/20">
                          <div className="pt-4">
                            <TrackingTimeline events={leg?.trackingEvents} />
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {trip.notes && (
            <Card className="p-4 border-yellow-200 bg-yellow-50">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">{trip.notes}</p>
              </div>
            </Card>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-border bg-muted/30 flex-shrink-0">
          {canAssign && (
            <Button className="w-full text-white bg-blue-600 hover:bg-blue-700" onClick={() => { onStatusChange(trip.id, "assigned"); onClose(); }}>
              <Users className="w-4 h-4 mr-2" />
              Assign Partner & Dispatch
            </Button>
          )}
          {canDispatch && (
            <div className="flex gap-2">
              <Button className="flex-1 text-white bg-indigo-600 hover:bg-indigo-700" onClick={() => { onStatusChange(trip.id, "dispatched"); onClose(); }}>
                <Truck className="w-4 h-4 mr-2" />
                Confirm Dispatch
              </Button>
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Edit Assignment
              </Button>
            </div>
          )}
          {trip.status === "dispatched" && (
            <Button className="w-full text-white bg-yellow-600 hover:bg-yellow-700" onClick={() => { onStatusChange(trip.id, "in_transit"); onClose(); }}>
              <Navigation className="w-4 h-4 mr-2" />
              Mark In Transit
            </Button>
          )}
          {trip.status === "at_hub" && (
            <Button className="w-full text-white bg-purple-600 hover:bg-purple-700" onClick={() => { onStatusChange(trip.id, "out_for_delivery"); onClose(); }}>
              <Truck className="w-4 h-4 mr-2" />
              Dispatch from Hub
            </Button>
          )}
          {canMarkDelivered && (
            <div className="flex gap-2">
              <Button className="flex-1 text-white bg-green-600 hover:bg-green-700" onClick={() => { onStatusChange(trip.id, "delivered"); onClose(); }}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirm Delivered
              </Button>
              <Button variant="destructive" className="flex-1 text-white bg-red-600 hover:bg-red-700" onClick={() => { onStatusChange(trip.id, "failed"); onClose(); }}>
                <XCircle className="w-4 h-4 mr-2" />
                Mark Failed
              </Button>
            </div>
          )}
          {trip.status === "delayed" && (
            <div className="flex gap-2">
              <Button className="flex-1 text-white bg-yellow-600 hover:bg-yellow-700" onClick={() => { onStatusChange(trip.id, "in_transit"); onClose(); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Resume / Reroute
              </Button>
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Update ETA
              </Button>
            </div>
          )}
          {(trip.status === "delivered" || trip.status === "failed") && (
            <p className="text-center text-sm text-muted-foreground py-1">
              Trip completed. No further actions.
            </p>
          )}
        </div>
      </div>
    </div>
  );
} */

// ─── Trip Detail Panel with Robust Tracking & Distance Mapping (new) ─────────────────
/* function TripDetailPanel({ trip, partners = [], vehicles = [], onClose, onStatusChange }) {
  const safeLegs = trip?.route_legs || [];
  const [expandedLeg, setExpandedLeg] = useState(safeLegs[0]?.id ?? null);
  
  const partner = partners.find((p) => p.id === trip?.partnerId);
  const vehicle = vehicles.find((v) => v.id === trip?.vehicleId);
  const canDispatch = trip?.status === "assigned";
  const canMarkDelivered = trip?.status === "out_for_delivery";
  const canAssign = trip?.status === "draft";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-2 sm:p-4 overflow-hidden">
      <div className="w-full max-w-4xl h-[90vh] max-h-[850px] flex flex-col overflow-hidden rounded-xl sm:rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0 bg-background">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-semibold text-base">{trip?.id}</h3>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${tripStatusConfig[trip?.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                {tripStatusConfig[trip?.status]?.label || trip?.status}
              </span>
              <Badge variant="outline" className="text-xs">
                {trip?.type === "hub_and_spoke" ? "Hub & Spoke" : "Direct"}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{trip?.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Orders", value: trip?.order_ids?.length?.toString() || "0" },
              { label: "Total Weight", value: trip?.total_weight || "N/A" },
              { label: "Total Value", value: `₹${trip?.total_value?.toLocaleString() || 0}` },
              { label: "Est. Delivery", value: trip?.estimated_delivery_time || "TBD" },
            ].map((s) => (
              <Card key={s.label} className="p-3 text-center">
                <p className="text-lg font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </Card>
            ))}
          </div>

          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Origin</p>
                <p className="text-sm font-medium">{trip?.origin}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Destination</p>
                <p className="text-sm font-medium">{trip?.destination}</p>
              </div>
            </div>
          </Card>

          // Route Legs & Tracking Accordion for Every Trip 
          {safeLegs.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Trip Route Legs, Distances & Universal Tracking</p>
              <div className="space-y-3">
                {safeLegs.map((leg) => {
                  const LegIcon = legStatusIcon[leg?.status]?.icon || Circle;
                  const isExpanded = expandedLeg === leg?.id;

                  console.log("Leg Stops:", leg?.stops);
                  
                  // Map backend stops or fallback to standard tracking events
                  const trackingEvents = (leg?.stops || []).length > 0 
                    ? (leg.stops || []).map(stop => ({
                        time: stop?.createdAt ? formatDate(stop.createdAt) : leg?.arrivalPlanned,
                        location: stop?.location?.address || "Checkpoint Location",
                        event: stop?.stopType === "PICKUP" ? "Pickup milestone reached" : "Dropoff checkpoint reached",
                        status: stop?.status === "COMPLETED" ? "completed" : stop?.status === "PENDING" ? "pending" : "current"
                      }))
                    : (leg?.trackingEvents || [
                        { time: leg?.departureActual || "In Progress", location: leg?.from || "Origin", event: "Leg Initiated", status: "completed" },
                        { time: leg?.arrivalPlanned || "Pending", location: leg?.to || "Destination", event: "Awaiting Arrival", status: "pending" }
                      ]);

                  return (
                    <Card key={leg?.id} className="overflow-hidden">
                      <button 
                        className="w-full flex items-center gap-3 p-4 hover:bg-accent/30 transition-colors text-left" 
                        onClick={() => setExpandedLeg(isExpanded ? null : leg?.id)}
                      >
                        <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                          leg?.status === "completed" ? "bg-green-100" :
                          leg?.status === "in_transit" ? "bg-yellow-100" :
                          leg?.status === "delayed" ? "bg-red-100" : "bg-gray-100"
                        }`}>
                          <LegIcon className={`w-4 h-4 ${legStatusIcon[leg?.status]?.color || 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              Leg {leg?.sequence} — {leg?.type?.replace(/_/g, " ")}
                            </span>
                            <span className="text-xs bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded">
                              📏 {leg?.distance || "Distance Mapped"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-sm">
                            <span className="font-medium truncate">{leg?.from}</span>
                            <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium truncate">{leg?.to}</span>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </button>
                      
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t bg-muted/20">
                          <div className="pt-4">
                            <TrackingTimeline events={trackingEvents} />
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-border bg-muted/30 flex-shrink-0">
          {canAssign && (
            <Button className="w-full text-white bg-blue-600 hover:bg-blue-700" onClick={() => { onStatusChange(trip?.id, "assigned"); onClose(); }}>
              <Users className="w-4 h-4 mr-2" /> Assign Partner & Dispatch
            </Button>
          )}
          {canDispatch && (
            <Button className="w-full text-white bg-indigo-600 hover:bg-indigo-700" onClick={() => { onStatusChange(trip?.id, "dispatched"); onClose(); }}>
              <Truck className="w-4 h-4 mr-2" /> Confirm Dispatch
            </Button>
          )}
          {canMarkDelivered && (
            <Button className="w-full text-white bg-green-600 hover:bg-green-700" onClick={() => { onStatusChange(trip?.id, "delivered"); onClose(); }}>
              <CheckCircle className="w-4 h-4 mr-2" /> Confirm Delivered
            </Button>
          )}
        </div>
      </div>
    </div>
  );
} */

//latest version of TripDetailPanel with improved tracking and distance mapping is provided above.
function TripDetailPanel({ trip, partners = [], vehicles = [], onClose, onStatusChange }) {
  const safeLegs = trip?.route_legs || [];
  const [expandedLeg, setExpandedLeg] = useState(safeLegs[0]?.id || safeLegs[0]?._id || null);

  // Modal states for interactive CTAs
  const [showEtaModal, setShowEtaModal] = useState(false);
  const [newEta, setNewEta] = useState(trip?.estimated_delivery_time || "");
  const [etaNotes, setEtaNotes] = useState(trip?.notes || "");

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(trip?.partner_id?._id || trip?.partnerId || "");
  const [selectedVehicle, setSelectedVehicle] = useState(trip?.vehicle_id?._id || trip?.vehicleId || "");

  const tripId = trip?._id || trip?.id;
  const partner = partners.find((p) => p._id === (trip?.partner_id?._id || trip?.partnerId) || p.id === trip?.partnerId);
  const vehicle = vehicles.find((v) => v._id === (trip?.vehicle_id?._id || trip?.vehicleId) || v.id === trip?.vehicleId);

  // API helper for CTAs
  const executeCtaAction = async (endpoint, payload, successMessage, nextStatus) => {
    try {
      const token = decryptData(sessionStorage.getItem("token"));
      const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.success) {
        toast.success(successMessage);
        if (nextStatus) onStatusChange(tripId, nextStatus);
        onClose();
      } else {
        toast.error(result.message || "Action failed");
      }
    } catch (err) {
      toast.error("Failed to perform action");
    }
  };

  // Tracking Update Action
/*   const handleUpdateStop = async (tripId, legId, stopId, newStatus) => {
    try {
      const token = decryptData(sessionStorage.getItem("token"));
      const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/update-tracking-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify({
          trip_id: tripId,
          leg_id: legId,
          stop_id: stopId,
          status: newStatus
        })
      });
      const result = await res.json();
      if (result.success) {
        toast.success(`Stop marked as ${newStatus}`);
        onStatusChange(tripId, trip.status); // Trigger refresh
      }
    } catch (error) {
      toast.error("Failed to update tracking status");
    }
  }; */

  const handleUpdateStop = async (tripId, legId, stopId, newStatus) => {
  if (!navigator.geolocation) {
    toast.error("Geolocation is not supported by your browser");
    return;
  }

  navigator.geolocation.getCurrentPosition(async (position) => {
    try {
      const { latitude, longitude } = position.coords;
      const token = decryptData(sessionStorage.getItem("token"));
      
      const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/update-tracking-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify({
          trip_id: tripId,
          leg_id: legId,
          stop_id: stopId,
          status: newStatus,
          location: {
            lat: latitude,
            lng: longitude,
            recorded_at: new Date().toISOString()
          }
        })
      });
      
      const result = await res.json();
      if (result.success) {
        toast.success(`Stop marked as ${newStatus} with location saved`);
        onStatusChange(tripId, trip.status); 
      }
    } catch (error) {
      toast.error("Failed to update tracking status");
    }
  }, (error) => {
    toast.error("Please allow location access to complete this stop.");
  }, { enableHighAccuracy: true });
};

  // CTA Action Functions
  const handleConfirmDispatch = () => executeCtaAction("confirm-dispatch", { trip_id: tripId }, "Trip confirmed and dispatched", "dispatched");
  const handleMarkInTransit = () => executeCtaAction("mark-intransit", { trip_id: tripId }, "Trip marked as In Transit", "in_transit");
  const handleDispatchFromHub = () => { onStatusChange(tripId, "out_for_delivery"); onClose(); };
  const handleConfirmDelivered = () => { onStatusChange(tripId, "delivered"); onClose(); };
  const handleMarkFailed = () => { onStatusChange(tripId, "failed"); onClose(); };
  const handleResumeReroute = () => executeCtaAction("resume-reroute", { trip_id: tripId, notes: "Rerouted after delay" }, "Trip resumed in transit", "in_transit");
  
  const handleSaveETA = () => {
    executeCtaAction("update-eta", { trip_id: tripId, estimated_delivery_time: newEta, notes: etaNotes }, "ETA updated successfully");
    setShowEtaModal(false);
  };

const handleSaveAssignment = () => {
  executeCtaAction(
    "create-or-update-trip",
    { 
      trip_id: tripId, 
      partner_id: selectedPartner, 
      vehicle_id: selectedVehicle,
      status: selectedPartner ? "assigned" : "draft" 
    },
    "Assignment updated successfully",
    selectedPartner ? "assigned" : "draft"
  );
  setShowAssignModal(false);
};

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-2 sm:p-4 overflow-hidden">
      <div className="w-full max-w-4xl h-[90vh] max-h-[850px] flex flex-col overflow-hidden rounded-xl sm:rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0 bg-background">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-semibold text-base">{trip?.trip_number || tripId}</h3>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${tripStatusConfig[trip?.status]?.color || "bg-gray-100 text-gray-800"}`}>
                {tripStatusConfig[trip?.status]?.label || trip?.status}
              </span>
              <Badge variant="outline" className="text-xs">
                {trip?.type === "hub_and_spoke" ? "Hub & Spoke" : "Direct"}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{trip?.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Orders", value: trip?.order_ids?.length?.toString() || "0" },
            { label: "Est. Total Distance", value: trip?.totalDistance ? `${trip.totalDistance} km` : "N/A" },
            { label: "Est. Duration", value: trip?.totalEstimatedMinutes ? formatDuration(trip.totalEstimatedMinutes) : "N/A" },
            { label: "Delivery Status", value: trip?.actual_delivery_time ? "Completed" : trip?.isDelayed ? "Delayed" : "On Track" },
          ].map((s) => (
            <Card key={s.label} className="p-3 text-center">
              <p className={`text-lg font-bold ${s.value === "Delayed" ? "text-red-600" : ""}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </Card>
          ))}
        </div>

          <Card className="p-4 space-y-4">
            {safeLegs.length > 0 ? (
              safeLegs.map((leg, idx) => (
                <div 
                  key={leg._id || leg.id || idx} 
                  className={`flex items-center justify-between gap-3 ${
                    idx !== safeLegs.length - 1 ? "border-b border-gray-100 pb-4" : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">
                      Origin {safeLegs.length > 1 ? `(Leg ${leg.sequence || idx + 1})` : ""}
                    </p>
                    <p className="text-sm font-medium truncate">
                      {leg.from || trip?.origin || "Central Hub"}
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center px-2 flex-shrink-0">
                    <ArrowRight className="w-4 h-4 text-muted-foreground mb-1" />
                    {leg.distance && (
                      <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        {leg.distance} km
                      </span>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0 text-right">
                    <p className="text-xs text-muted-foreground">
                      Destination {safeLegs.length > 1 ? `(Leg ${leg.sequence || idx + 1})` : ""}
                    </p>
                    <p className="text-sm font-medium truncate">
                      {leg.to || trip?.destination || "Dropoff Points"}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              /* Fallback if no legs are defined yet */
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Origin</p>
                  <p className="text-sm font-medium truncate">{trip?.origin || "Central Hub"}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0 text-right">
                  <p className="text-xs text-muted-foreground">Destination</p>
                  <p className="text-sm font-medium truncate">{trip?.destination || "Dropoff Points"}</p>
                </div>
              </div>
            )}
          </Card>

          {/* Assigned Partner & Vehicle */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="p-4">
              <p className="text-xs text-muted-foreground mb-1 font-semibold uppercase">Assigned Partner</p>
              {partner ? (
                <div>
                  <p className="font-semibold text-sm">{partner.name}</p>
                  <p className="text-xs text-muted-foreground">📱 {partner.mobile || partner.phone}</p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No partner assigned</p>
              )}
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted-foreground mb-1 font-semibold uppercase">Assigned Vehicle</p>
              {vehicle ? (
                <div>
                  <p className="font-semibold text-sm">{partner?.vehicle_number || vehicle.registration}</p>
                  <p className="text-xs text-muted-foreground">Type: {partner?.vehicle_type || vehicle.type}</p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No vehicle assigned</p>
              )}
            </Card>
          </div>

          {/* Route Legs & Tracking */}
          {safeLegs.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Route Legs & Waypoints</p>
              {safeLegs.map((leg) => {
                const LegIcon = legStatusIcon[leg?.status]?.icon || Circle;
                const isExpanded = expandedLeg === (leg?._id || leg?.id);
                
                // Moved tracking events INSIDE the leg mapping scope
                const mappedTrackingEvents = (leg?.stops || []).map((stop) => ({
                  id: stop._id,
                  legId: leg._id || leg.id,
                  time: stop.actualTime ? `Actual: ${formatDate(stop.actualTime)}` : (stop.plannedTime ? `Planned: ${formatDate(stop.plannedTime)}` : "Pending"),
                  location: stop?.location?.address || "Checkpoint Location",
                  event: stop?.stopType === "PICKUP" ? "Pickup milestone reached" : "Dropoff checkpoint reached",
                  status: stop?.status === "COMPLETED" ? "completed" : stop?.status === "PENDING" ? "pending" : "current",
                  isDelayed: stop?.isDelayed,
                  delayMinutes: stop?.delayMinutes,
                  isActionable: stop?.status !== "COMPLETED"
                }));

                return (
                  <Card key={leg?._id || leg?.id} className="overflow-hidden">
                    <button
                      className="w-full flex items-center gap-3 p-4 hover:bg-accent/30 transition-colors text-left"
                      onClick={() => setExpandedLeg(isExpanded ? null : (leg?._id || leg?.id))}
                    >
                      <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                        leg?.status === "completed" ? "bg-green-100 text-green-700" :
                        leg?.status === "in_transit" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"
                      }`}>
                        <LegIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Leg {leg?.sequence} — {leg?.type}</p>
                        <p className="text-sm font-medium truncate">{leg?.from} → {leg?.to}</p>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    {isExpanded && (
                      <div className="p-4 border-t bg-muted/10">
                        <TrackingTimeline
                          events={mappedTrackingEvents}
                          onUpdateEvent={(legId, stopId, status) => handleUpdateStop(tripId, legId, stopId, status)}
                        />
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {trip?.notes && (
            <Card className="p-3 bg-amber-50 border-amber-200">
              <div className="flex gap-2 text-amber-800 text-xs">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                <p>{trip.notes}</p>
              </div>
            </Card>
          )}
        </div>

        {/* Footer CTAs */}
        <div className="p-4 border-t border-border bg-muted/30 flex-shrink-0">
          {trip?.status === "draft" && (
            <Button className="w-full text-white bg-blue-600 hover:bg-blue-700" onClick={() => setShowAssignModal(true)}>
              <Users className="w-4 h-4 mr-2" /> Assign Partner & Dispatch
            </Button>
          )}

          {trip?.status === "assigned" && (
            <div className="flex gap-2">
              <Button className="flex-1 text-white bg-indigo-600 hover:bg-indigo-700" onClick={handleConfirmDispatch}>
                <Truck className="w-4 h-4 mr-2" /> Confirm Dispatch
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowAssignModal(true)}>
                Edit Assignment
              </Button>
            </div>
          )}

          {trip?.status === "dispatched" && (
            <Button className="w-full text-white bg-yellow-600 hover:bg-yellow-700" onClick={handleMarkInTransit}>
              <Navigation className="w-4 h-4 mr-2" /> Mark In Transit
            </Button>
          )}

          {trip?.status === "at_hub" && (
            <Button className="w-full text-white bg-purple-600 hover:bg-purple-700" onClick={handleDispatchFromHub}>
              <Truck className="w-4 h-4 mr-2" /> Dispatch from Hub
            </Button>
          )}

          {trip?.status === "out_for_delivery" && (
            <div className="flex gap-2">
              <Button className="flex-1 text-white bg-green-600 hover:bg-green-700" onClick={handleConfirmDelivered}>
                <CheckCircle className="w-4 h-4 mr-2" /> Confirm Delivered
              </Button>
              <Button variant="destructive" className="flex-1" onClick={handleMarkFailed}>
                <XCircle className="w-4 h-4 mr-2" /> Mark Failed
              </Button>
            </div>
          )}

          {trip?.status === "delayed" && (
            <div className="flex gap-2">
              <Button className="flex-1 text-white bg-yellow-600 hover:bg-yellow-700" onClick={handleResumeReroute}>
                <RefreshCw className="w-4 h-4 mr-2" /> Resume / Reroute
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowEtaModal(true)}>
                Update ETA
              </Button>
            </div>
          )}

          {(trip?.status === "delivered" || trip?.status === "failed") && (
            <p className="text-center text-xs text-muted-foreground py-1">Trip completed. No further lifecycle actions.</p>
          )}
        </div>
      </div>

      {/* Modal: Update ETA */}
      {showEtaModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md p-5 bg-white space-y-4 shadow-xl">
            <h4 className="font-semibold text-sm">Update Estimated Delivery & Notes</h4>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">New Estimated Delivery</label>
              <Input type="datetime-local" value={newEta} onChange={(e) => setNewEta(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Delay / Reroute Reason</label>
              <textarea
                className="w-full border rounded-md p-2 text-xs"
                rows={3}
                value={etaNotes}
                onChange={(e) => setEtaNotes(e.target.value)}
                placeholder="Reason for schedule change..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowEtaModal(false)}>Cancel</Button>
              <Button size="sm" className="bg-blue-600 text-white" onClick={handleSaveETA}>Save Changes</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal: Edit Partner & Vehicle Assignment */}
      {showAssignModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md p-5 bg-white space-y-4 shadow-xl">
            <h4 className="font-semibold text-sm">Assign Partner & Vehicle</h4>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Delivery Partner</label>
              <select
                className="w-full border rounded-md p-2 text-xs"
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
              >
                <option value="">-- Choose Partner --</option>
                {partners.map((p) => (
                  <option key={p._id || p.id} value={p._id || p.id}>{p.name} ({p.mobile || p.phone})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Vehicle</label>
              <select
                className="w-full border rounded-md p-2 text-xs"
                value={selectedVehicle}
                onChange={(e) => setSelectedVehicle(e.target.value)}
              >
                <option value="">-- Choose Vehicle --</option>
                {vehicles.map((v) => (
                  <option key={v._id || v.id} value={v._id || v.id}>{v.vehicle_number || v.registration} ({v.vehicle_type || v.type})</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAssignModal(false)}>Cancel</Button>
              <Button size="sm" className="bg-blue-600 text-white" onClick={handleSaveAssignment}>Save Assignment</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}


// ─── Trip Row ─────────────────────────────────────────────────────────────
function TripRow({ trip, partners, onSelect, onQuickDispatch }) {
  const partner = partners.find((p) => p.id === trip.partnerId);
  const cfg = tripStatusConfig[trip.status];
  
  return (
    <div className={`flex items-center gap-4 p-4 border rounded-lg hover:bg-accent/30 transition-colors ${trip.status === "delayed" ? "border-red-200 bg-red-50/30" : ""}`}>
      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg?.dot}`} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className=" text-sm font-semibold">{trip?.trip_number}</span>
          {/* Fix 1: Pass the trip variable directly, not as a string */}
          <TripEtaDisplay trip={trip} />
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg?.color}`}>
            {cfg?.label}
          </span>
          <span className="text-xs border rounded-full px-2 py-0.5 text-muted-foreground">
            {trip.type === "hub_and_spoke" ? "Hub & Spoke" : "Direct"}
          </span>
          {trip.priority === "high" && (<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">High Priority</span>)}
        </div>
        <p className="text-sm font-medium mt-0.5 truncate">{trip.name}</p>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          <span>{trip?.order_ids?.length} order{trip?.order_ids?.length !== 1 ? "s" : ""}</span>
          <span>·</span>
          <span>{trip?.total_weight}</span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {trip.route_legs[0]?.from} → {trip.route_legs[0]?.to}
          </span>
          {partner && (
            <>
              <span>·</span>
              <span>{partner.name}</span>
            </>
          )}
        </div>
      </div>

      <div className="text-right flex-shrink-0">
        <p className="font-semibold text-sm">₹{trip?.total_value?.toLocaleString()}</p>
        {/* Fix 2: Format the estimated delivery object safely */}
        <p className="text-xs text-muted-foreground">
          ETA: {formatEstimatedTime(trip.estimatedDelivery || trip.estimated_delivery_time)}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {trip.status === "assigned" && (
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 px-3" onClick={() => onQuickDispatch(trip._id)}>
            <Truck className="w-3.5 h-3.5 mr-1" />
            Dispatch
          </Button>
        )}
        {trip.status === "draft" && (
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3" onClick={() => onSelect(trip)}>
            <Users className="w-3.5 h-3.5 mr-1" />
            Assign
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => onSelect(trip)}>
          <Eye className="w-3.5 h-3.5 mr-1" />
          Track
        </Button>
      </div>
    </div>
  );
}

// ─── Fleet & Partners View ────────────────────────────────────────────────────
/* function FleetView({  fleets , partners, vehicles }) {
const [fleets,setFleets] = useState();
const [savingPartner, setSavingPartner] = useState(false);
const [showPopupMessages, setShowPopupMessages] = useState(false);
const [popupMessage, setPopupMessage] = useState('');
const [popupType, setPopupType] = useState('');
const [partnersLoading, setPartnersLoading] = useState(false);
const [selectedVehicleType, setSelectedVehicleType] = useState("Bike");
const [vehicleCategories, setVehicleCategories] = useState([]);
const [deliveryPartners, setDeliveryPartners] = useState([]);
const [showAddPartnerPopup, setShowAddPartnerPopup] = useState(false);
const [showPartnerDetailsPopup, setShowPartnerDetailsPopup] = useState(false);
const [showAddPlatform, setShowAddPlatform] = useState(false);
const [newPlatform, setNewPlatform] = useState("");
const shipmentData = {}
const [partnerForm, setPartnerForm] = useState({
  name: "",
  mobile: "",
  platform: "",
  vehicle_type: "",
  vehicle_number: "",
  delivery_charge: "",
});

useEffect(() => {
  getAllDeliveryPartners();
}, []);

  const getAllDeliveryPartners = async () => {
    try {
      setPartnersLoading(true);
  
  const encryptedToken = sessionStorage.getItem('token');
                  const token = decryptData(encryptedToken);
                  const encryptedhubId = sessionStorage.getItem('hubId');
                  const hubId = decryptData(encryptedhubId);
  
      if (!hubId) return;
  
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/get-all-delivery-partners?hubId=${hubId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token || "",
          },
        }
      );
  
      if (!response.ok) throw new Error("Failed to fetch delivery partners");
  
      const result = await response.json();
    console.log(result)
      // ✅ IMPORTANT
      setFleets(result?.data?.fleets)
      setDeliveryPartners(result?.data?.partners || []);
      setVehicleCategories(
        (result?.data?.vehicle_types || []).map(normalizeVehicleType)
      );
  
    } catch (err) {
      console.error("Delivery partner fetch failed", err);
    } finally {
      setPartnersLoading(false);
    }
  };

  const normalizeVehicleType = (type = "") => {
  if (!type || typeof type !== "string") return "";

  return type
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
};


const handleCreatePartner = async () => {
  try {
    setSavingPartner(true);

    // Basic validation
    if (!partnerForm.name || !partnerForm.mobile) {
      setPopupMessage("Please fill in required fields (Name and Mobile)");
      setPopupType("400");
      setShowPopupMessages(true);
      return;
    }

    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(partnerForm.mobile)) {
      setPopupMessage("Mobile number must be exactly 10 digits");
      setPopupType("400");
      setShowPopupMessages(true);
      return;
    }

    const encryptedhubId = sessionStorage.getItem('hubId');
    const hubId = decryptData(encryptedhubId);

    if (!hubId) {
      setPopupMessage("hub ID not found. Please log in again.");
      setPopupType("401");
      setShowPopupMessages(true);
      return;
    }

    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    // Prepare the payload with correct types (convert mobile to number if backend expects it)
    const partnerData = {
      name: partnerForm.name,
      mobile: Number(partnerForm.mobile),  // Convert to number for backend schema
      email: partnerForm.email || "",
      // platform: partnerForm.platform || "brickbucket",
      vehicle_type: partnerForm.vehicle_type || "",
      vehicle_number: partnerForm.vehicle_number || "",
      hubId: hubId, 
      roleId:15, 
    };

    console.log("Creating partner with payload:", partnerData);

    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/create-delivery-partner`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token || "",
        },
        body: JSON.stringify(partnerData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to create delivery partner");
    }

    const result = await response.json();
    setPopupMessage("Delivery partner created successfully");
    setPopupType("200or201");
    setShowPopupMessages(true);
    setTimeout(() => {
      setShowPopupMessages(false);
    }, 3000);
    await getAllDeliveryPartners();

   if (result?.success || result?.data) {
      setShowAddPartnerPopup(false);
      setShowPartnerDetailsPopup(false);  
      setPartnerForm({
        name: "",
        mobile: "",
        email: "",
        // platform: "",
        vehicle_type: "",
        vehicle_number: "",
      });
      setShowAddPlatform(false);
      setNewPlatform("");

      await getAllDeliveryPartners();

      
    }
  } catch (error) {
    console.error("Failed to create partner:", error);
    setPopupMessage("Failed to create delivery partner. Please try again.");
    setPopupType("500");
    setShowPopupMessages(true);
  } finally {
    setSavingPartner(false);
  }
};

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Delivery Partners & 3PL</h4>
          <Button size="sm" variant="outline" onClick={() => setShowAddPartnerPopup(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Partner
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {fleets.map((f) => {
            const typeCfg = partnerTypeConfig[f];
            const TypeIcon = typeCfg?.icon;
            console.log(f)
            return (
              <Card key={f} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${typeCfg.color.replace("text-", "bg-").replace("-700", "-100").replace("-800", "-100")}`}>
                      <TypeIcon className={`w-4 h-4 ${typeCfg.color.split(" ")[1]}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{f}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeCfg.color}`}>
                        {typeCfg.label}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <span className="text-xs font-medium">{f?.rating}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div>
                    <p className="text-base font-bold text-blue-600">{f?.activeTrips}</p>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </div>
                  <div>
                    <p className="text-base font-bold">{f?.completedTrips}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div>
                    <p className={`text-base font-bold ${f?.onTimeRate >= 90 ? "text-green-600" : f?.onTimeRate >= 80 ? "text-yellow-600" : "text-red-600"}`}>
                      {f?.onTimeRate}%
                    </p>
                    <p className="text-xs text-muted-foreground">On-Time</p>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    <span>{f?.contactName}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {f?.coverageZones.slice(0, 2).map((z) => (
                      <span key={z} className="bg-muted px-1.5 py-0.5 rounded text-xs">{z}</span>
                    ))}
                    {f?.coverageZones.length > 2 && (
                      <span className="bg-muted px-1.5 py-0.5 rounded text-xs">+{f?.coverageZones.length - 2}</span>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Vehicle Fleet</h4>
          <div className="flex gap-2">
            <Badge variant="outline" className="text-xs">
              {vehicles.filter((v) => v.status === "available").length} available
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {vehicles.filter((v) => v.status === "in_use").length} in use
            </Badge>
          </div>
        </div>
        <div className="space-y-2">
          {vehicles.map((v) => {
            const statusCfg = vehicleStatusConfig[v.status];
            const partner = partners.find((p) => p.id === v.partnerId);
            return (
              <div key={v.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-accent/30 transition-colors">
                <span className="text-xl">{vehicleTypeIcon[v.type]}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{v.registration}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusCfg.color}`}>
                      {statusCfg.label}
                    </span>
                    {partner && (<span className="text-xs text-muted-foreground">{partner.name}</span>)}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    <span>{v.driver}</span>
                    <span>·</span>
                    <span>{v.capacity}</span>
                    {v.currentLocation && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {v.currentLocation}
                        </span>
                      </>
                    )}
                    {v.assignedTrip && (
                      <>
                        <span>·</span>
                        <span className="text-blue-600 font-medium">{v.assignedTrip}</span>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground flex-shrink-0">{v.lastUpdated}</p>
              </div>
            );
          })}
        </div>
      </div>
      {showAddPartnerPopup && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[999] p-4 transition-all">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-slate-100 max-h-[90vh] overflow-y-auto">
            
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h3 className="text-xl font-bold text-slate-800">New Delivery Partner</h3>
                <p className="text-xs text-slate-500 mt-1">Register a new driver to your network</p>
              </div>
              <button 
                onClick={() => setShowAddPartnerPopup(false)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400"
              >
                <X size={20} />
              </button>
            </div>
      
            <div className="p-5 space-y-4">
              
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700 ml-1">Full Name</label>
                  <input
                    placeholder="e.g. John Doe"
                    value={partnerForm.name}
                    onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-sm"
                  />
                </div>
      
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-slate-700 ml-1">Mobile Number</label>
                    <input
                      placeholder="10-digit mobile number"
                      value={partnerForm.mobile}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPartnerForm({ ...partnerForm, mobile: numericValue });
                      }}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-sm"
                    />
                  </div>
      
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-slate-700 ml-1">Email</label>
                    <input
                      type="email"
                      placeholder="e.g. john@example.com"
                      value={partnerForm.email || ""}
                      onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-sm"
                    />
                  </div>
                </div>
              </div>
      
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700 ml-1">Vehicle Type</label>
                  <input
                    placeholder="e.g. Bike"
                    value={partnerForm.vehicle_type}
                    onChange={(e) => setPartnerForm({ ...partnerForm, vehicle_type: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700 ml-1">Vehicle Number</label>
                  <input
                    placeholder="e.g. AB 01 CD 1234"
                    value={partnerForm.vehicle_number}
                    onChange={(e) => setPartnerForm({ ...partnerForm, vehicle_number: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2 focus:ring-1  focus:ring-blue-500 focus:border-blue-500 outline-none transition-all  placeholder:text-sm"
                  />
                </div>
              </div>
            </div>
      
            <div className="px-6 py-2 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 text-sm">
              <button
                onClick={() => setShowAddPartnerPopup(false)}
                className="px-4 py-2 text-slate-600 font-semibold border border-slate-300 bg-slate-100 hover:bg-slate-200 transition-colors rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePartner}
                disabled={savingPartner}
                className="px-4 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 active:scale-95 disabled:opacity-50 disabled:active:scale-100 transition-all"
              >
                {savingPartner ? 'Creating...' : 'Save Partner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPartnerDetailsPopup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden">
      
            <div className="flex justify-between items-center px-6 py-4 border-b bg-gradient-to-r from-gray-50 to-white">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">
                  Delivery Partner Details
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  View assigned or notified delivery partners
                </p>
              </div>
      
              <button
                onClick={() => setShowPartnerDetailsPopup(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
      
            <div className="p-6 bg-gray-50">
      
              <div className="flex justify-between items-center mb-6">
                <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
               Partner Response Status
                </div>
      
                <span
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold shadow-sm
                    ${
                      shipmentData?.delivery_partner?.length > 0
                        ? "bg-green-100 text-green-700"
                        : shipmentData?.notifiedRiders?.length > 0
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-gray-200 text-gray-600"
                    }`}
                >
                  {shipmentData?.delivery_partner?.length > 0
                    ? "Accepted"
                    : shipmentData?.notifiedRiders?.length > 0
                    ? "Waiting for acceptance"
                    : "Not Assigned"}
                </span>
              </div>
      
              {shipmentData?.delivery_partner?.length > 0 ? (
                shipmentData.delivery_partner.map((partner, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-5 text-sm">
      
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Partner Name</div>
                        <div className="font-semibold text-gray-900">
                          {partner.name || "—"}
                        </div>
                      </div>
      
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Mobile</div>
                        <div className="font-semibold text-gray-900">
                          {partner.mobile || "—"}
                        </div>
                      </div>
      
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Vehicle Number</div>
                        <div className="font-semibold text-gray-900">
                          {partner.vehicle_number || "—"}
                        </div>
                      </div>
      
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Vehicle Type</div>
                        <div className="font-semibold text-gray-900">
                          {partner.vehicle_type || "—"}
                        </div>
                      </div>
      
                      {partner.platform && (
                        <div>
                          <div className="text-xs text-gray-400 mb-1">Platform</div>
                          <div className="font-semibold text-gray-900">
                            {partner.platform}
                          </div>
                        </div>
                      )}
      
                      {partner.delivery_charge !== undefined && (
                        <div>
                          <div className="text-xs text-gray-400 mb-1">Delivery Charge</div>
                          <div className="font-semibold text-green-600">
                            ₹{partner.delivery_charge}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
      
                  {shipmentData?.notifiedRiders?.length > 0 ? (
                    <>
                      <div className="text-sm font-semibold text-gray-700">
                        Waiting for rider acceptance
                      </div>
      
                      <p className="text-xs text-gray-500 mt-2">
                        {shipmentData.notifiedRiders.length} rider(s) notified.
                      </p>
      
                      <div className="mt-4 flex flex-wrap gap-2 justify-center">
                        {shipmentData.notifiedRiders.slice(0, 3).map((rider, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium"
                          >
                            {rider.name} ({rider.mobile})
                          </div>
                        ))}
      
                        {shipmentData.notifiedRiders.length > 3 && (
                          <div className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-600">
                            +{shipmentData.notifiedRiders.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-semibold text-gray-700">
                        No delivery partner assigned
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Notify riders to assign a delivery partner.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} */

function FleetView({ fleets = [], partners = [], vehicles = [], onRefresh }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFleet, setSelectedFleet] = useState(null);
  
  // Modal, Edit & Popup State
  const [showAddPartnerPopup, setShowAddPartnerPopup] = useState(false);
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popup, setPopup] = useState({ show: false, message: "", type: "success" });
  
  const [partnerForm, setPartnerForm] = useState({
    name: "",
    mobile: "",
    email: "",
    vehicle_number: "",
    vehicle_type: "",
    platform: "brickbucket",
    fleet: "brickbucket"
  });

  // Filter partners by Search and Selected Fleet
  const filteredPartners = partners.filter((p) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      p.name?.toLowerCase().includes(searchLower) ||
      p.mobile?.includes(searchQuery) ||
      p.vehicle_number?.toLowerCase().includes(searchLower) ||
      p.email?.toLowerCase().includes(searchLower);
    
    const partnerFleet = p.fleet || p.type || "Brickbucket"; 
    const matchesFleet = selectedFleet ? partnerFleet.toLowerCase() === selectedFleet.toLowerCase() : true;
    
    return matchesSearch && matchesFleet;
  });

  // Pagination hook (5 per page)
  const {
    currentItems: paginatedPartners,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
  } = usePagination(filteredPartners, 5);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    handlePageChange(1);
  };

  const toggleFleetFilter = (fleet) => {
    setSelectedFleet(prev => (prev === fleet ? null : fleet));
    handlePageChange(1);
  };

  const resetForm = () => {
    setPartnerForm({ name: "", mobile: "", email: "", vehicle_number: "", vehicle_type: "", platform: "brickbucket", fleet: "brickbucket" });
    setEditingPartnerId(null);
  };

  const handleEditClick = (partner) => {
    setPartnerForm({
      name: partner.name || "",
      mobile: partner.mobile || "",
      email: partner.email || "",
      vehicle_number: partner.vehicle_number || "",
      vehicle_type: partner.vehicle_type || "",
      platform: partner.platform || "brickbucket",
      fleet: partner.fleet || "brickbucket"
    });
    setEditingPartnerId(partner._id || partner.id);
    setShowAddPartnerPopup(true);
  };

  // API Submission for Create & Update
  const handleSavePartner = async () => {
    try {
      setIsSubmitting(true);
      if (!partnerForm.name || !partnerForm.mobile || !partnerForm.vehicle_number || !partnerForm.vehicle_type) {
        setPopup({ show: true, message: "Please fill all required fields", type: "error" });
        return;
      }

      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);
      const encryptedhubId = sessionStorage.getItem('hubId');
      const hubId = decryptData(encryptedhubId);

      const payload = {
        ...partnerForm,
        hubId: hubId,
        roleId: 15,
      };

      if (editingPartnerId) {
        payload.partner_id = editingPartnerId; 
      }

      const endpoint = editingPartnerId 
        ? "update-delivery-partner" 
        : "create-delivery-partner";

      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/${endpoint}`, {
        method: editingPartnerId ? "PUT" : "POST", 
        headers: { "Content-Type": "application/json", "x-token": token || "" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.success || response.ok) {
        setPopup({ show: true, message: `Delivery partner ${editingPartnerId ? "updated" : "created"} successfully!`, type: "success" });
        setShowAddPartnerPopup(false);
        resetForm();
        if (onRefresh) onRefresh();
      } else {
        setPopup({ show: true, message: result.message || "Failed to save partner", type: "error" });
      }
    } catch (error) {
      setPopup({ show: true, message: "Network error. Please try again.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getVehicleIcon = (type) => {
    if (!type) return "🚗";
    const key = type.toLowerCase();
    return vehicleTypeIcon[key] || "🚗";
  };

  return (
    <div className="space-y-6 relative">
      {/* ── Custom Status Popup Overlay ── */}
      {popup.show && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm w-full text-center animate-in zoom-in duration-200">
            {popup.type === 'success' ? (
              <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            ) : (
              <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            )}
            <h3 className="font-bold text-xl text-gray-900 mb-2">
              {popup.type === 'success' ? 'Success!' : 'Error'}
            </h3>
            <p className="text-gray-600 text-sm mb-6">{popup.message}</p>
            <Button 
              className={`w-full ${popup.type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`} 
              onClick={() => setPopup({ show: false, message: "", type: "success" })}
            >
              Close
            </Button>
          </div>
        </div>
      )}

{/* ── Fleet Categories ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Delivery Fleets & 3PL</h4>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {fleets.map((f) => {
            const isSelected = selectedFleet === f;
            
            // Restore the dynamic icon and color mapping
            const typeCfg = partnerTypeConfig[f] || { label: f, color: "bg-gray-100 text-gray-700", icon: Truck };
            const TypeIcon = typeCfg.icon;

            return (
              <Card 
                key={f} 
                className={`p-4 cursor-pointer transition-all ${isSelected ? "ring-2 ring-blue-500 bg-blue-50/20" : "hover:border-gray-300"}`}
                onClick={() => toggleFleetFilter(f)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${typeCfg.color.replace("text-", "bg-").replace("-700", "-100")}`}>
                      <TypeIcon className={`w-4 h-4 ${typeCfg.color.split(" ")[1]}`} />
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${isSelected ? "text-blue-700" : ""}`}>{f}</p>
                      {/* Restored the dynamic label badge */}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeCfg.color}`}>
                        {typeCfg.label}
                      </span>
                    </div>
                  </div>
                  {isSelected && <CheckCircle className="w-4 h-4 text-blue-500" />}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── Partners List & Search ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Registered Partners & Drivers</h4>
          <Button size="sm" onClick={() => { resetForm(); setShowAddPartnerPopup(true); }} className="bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Add Partner
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              className="pl-9 bg-white" 
              placeholder="Search by name, phone, or vehicle..." 
              value={searchQuery} 
              onChange={handleSearchChange} 
            />
          </div>
          {selectedFleet && (
            <Badge variant="secondary" className="flex items-center gap-1 bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer" onClick={() => toggleFleetFilter(selectedFleet)}>
              Filtered by: {selectedFleet} <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
        </div>

        <div className="space-y-2 min-h-[300px]">
          {paginatedPartners.length === 0 ? (
            <div className="text-center py-10 border rounded-lg bg-gray-50 text-gray-500 text-sm">
              No delivery partners found.
            </div>
          ) : (
            paginatedPartners.map((p) => (
              <div key={p._id || p.id} className="flex items-center justify-between gap-4 p-3 border rounded-lg bg-card hover:bg-accent/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{p.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-mono uppercase flex items-center gap-1">
                      {getVehicleIcon(p.vehicle_type)} {p.vehicle_type || "Vehicle"}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border">
                      {p.platform || p.fleet || "Internal"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3"/> {p.mobile}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1 font-mono">{p.vehicle_number || "N/A"}</span>
                    {p.email && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-1">✉️ {p.email}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Edit Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleEditClick(p)} 
                  className="flex-shrink-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={5}
              onPageChange={handlePageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              showInfo={true}
              itemName="Partners"
            />
          </div>
        )}
      </div>

      {/* ── Add / Edit Partner Modal ── */}
      {showAddPartnerPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[990] p-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="font-bold text-gray-800">{editingPartnerId ? "Edit Delivery Partner" : "Add Delivery Partner"}</h3>
                <p className="text-xs text-gray-500">{editingPartnerId ? "Update driver details" : "Register a new fleet driver"}</p>
              </div>
              <button onClick={() => { setShowAddPartnerPopup(false); resetForm(); }} className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
      
            <div className="p-5 overflow-y-auto space-y-4 text-sm">
              <div className="space-y-3">
                <div>
                  <label className="font-medium text-gray-700 block mb-1 text-xs">Full Name *</label>
                  <Input placeholder="Enter driver name" value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Mobile Number *</label>
                    <Input placeholder="10-digit mobile" maxLength={10} value={partnerForm.mobile} onChange={(e) => setPartnerForm({ ...partnerForm, mobile: e.target.value.replace(/\D/g, '') })} />
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Email Address</label>
                    <Input type="email" placeholder="Email" value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t pt-3">
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Vehicle Type *</label>
                    <select 
                      className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 bg-white" 
                      value={partnerForm.vehicle_type} 
                      onChange={(e) => setPartnerForm({ ...partnerForm, vehicle_type: e.target.value })}
                    >
                      <option value="">Select Vehicle Type</option>
                      {vehicles.map((vType, idx) => {
                        const value = typeof vType === 'string' ? vType : (vType.name || vType.type || "");
                        return (
                          <option key={idx} value={value}>
                            {value}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Vehicle Number *</label>
                    <Input placeholder="e.g. MH 01 AB 1234" value={partnerForm.vehicle_number} onChange={(e) => setPartnerForm({ ...partnerForm, vehicle_number: e.target.value.toUpperCase() })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Fleet *</label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={partnerForm.fleet} onChange={(e) => setPartnerForm({ ...partnerForm, fleet: e.target.value })}>
                      <option value="brickbucket">Brickbucket</option>
                      <option value="on demand">On Demand</option>
                      <option value="supplier">Supplier</option>
                      <option value="3 pl">3 PL</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Platform *</label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={partnerForm.platform} onChange={(e) => setPartnerForm({ ...partnerForm, platform: e.target.value })}>
                      <option value="brickbucket">Brickbucket</option>
                      <option value="own">Own</option>
                      <option value="ola">Ola</option>
                      <option value="uber">Uber</option>
                      <option value="rapido">Rapido</option>
                      <option value="shadowfax">Shadowfax</option>
                      <option value="porter">Porter</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
      
            <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-3 flex-shrink-0">
              <Button variant="outline" onClick={() => { setShowAddPartnerPopup(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleSavePartner} disabled={isSubmitting} className="bg-blue-600 text-white hover:bg-blue-700 min-w-[120px]">
                {isSubmitting ? 'Saving...' : (editingPartnerId ? 'Update Partner' : 'Save Partner')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Order Tracking View ───────────────────────────────────────────────────────
/* function OrderTrackingView({ trips }) {
  const [search, setSearch] = useState("");
  const [expandedOrders, setExpandedOrders] = useState({});
  const [paginatedOrders,setPaginatedOrders] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages,setTotalPages] = useState(1);
  const [totalItems,setTotalItems] = useState(0);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const encryptedToken = sessionStorage.getItem("token");
        const token = decryptData(encryptedToken);
        
        const res = await fetch(
          `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/all-orders-tracking?page=${currentPage}&limit=5&search_order_number=${search}`, 
          { headers: { "Content-Type": "application/json", "x-token": token } }
        );
        const result = await res.json();

        console.log(result)
        
        //if (result.success) {
          setPaginatedOrders(result.data);
          if(result.pagination) {
          setTotalPages(result.pagination.totalPages);
          setTotalItems(result.pagination.total);
          }
        //}
      } catch (err) {
        console.error(err);
      }
    };
    
    const delay = setTimeout(fetchTracking, 300);
    return () => clearTimeout(delay);
  }, [currentPage, search]);

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

    const handlePageChange = (page) => setCurrentPage(page);
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const handlePreviousPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    //resetToFirstPage();
  };

  return (
    <div className="flex flex-col h-[calc(100vh-160px)]">
      <div className="flex-shrink-0 space-y-4 mb-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={5}
          showInfo={true}
          hideControls={true}
          itemName="orders"
        />

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by order ID or customer..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-2 space-y-3 min-h-0">
        {console.log(paginatedOrders)}
        {(paginatedOrders?.length === 0 || undefined) ? (
          <div className="text-center rounded-lg border border-gray-100 py-16 flex flex-col items-center justify-center text-gray-500 mt-4">
            <Package className="w-8 h-8 mb-2 text-gray-400" />
            <p className="text-sm font-medium">No Orders Found</p>
          </div>
        ) : (
          paginatedOrders?.map((o) => {
            const isExpanded = expandedOrders[o.orderId];

            // Determine overall order status based on trips (simplified logic)
            const overallStatus = o?.trips?.length > 0 ? o?.trips[0]?.status : "draft";
            const cfg = tripStatusConfig[overallStatus] || tripStatusConfig.draft;

            return (
              <Card key={o.orderId} className="overflow-hidden shadow-sm">
                {console.log(o)}
                <div
                  className="p-4 bg-background border-b border-border hover:bg-accent/10 transition-colors cursor-pointer"
                  onClick={() => toggleOrderExpand(o.orderId)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div
                        className={`p-2 rounded-lg flex-shrink-0 ${
                          overallStatus === "delivered" ? "bg-green-100" :
                          overallStatus === "delayed" ? "bg-red-100" :
                          overallStatus === "out_for_delivery" ? "bg-purple-100" :
                          overallStatus === "in_transit" ? "bg-yellow-100" :
                          "bg-gray-100"
                        }`}
                      >
                        <Package
                          className={`w-5 h-5 ${
                            overallStatus === "delivered" ? "text-green-600" :
                            overallStatus === "delayed" ? "text-red-600" :
                            overallStatus === "out_for_delivery" ? "text-purple-600" :
                            overallStatus === "in_transit" ? "text-yellow-600" :
                            "text-gray-500"
                          }`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-1">
                          <span className="font-mono text-base font-bold text-foreground">
                            {o.orderNumber}
                          </span>
                          <span
                            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}
                          >
                            {cfg.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                          <span className="font-medium text-gray-900 flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" /> {o.customer}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> {o.destination}
                          </span>
                          <span className="flex items-center gap-1 font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                            {o?.deliveryGroups?.length} Delivery Group
                            {o?.deliveryGroups?.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4 mt-2">
                      <button className="p-1.5 hover:bg-muted rounded-md transition-colors text-muted-foreground border">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="bg-slate-50/50 p-4 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Route className="w-4 h-4 text-muted-foreground" />
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                        Group Tracking Details
                      </p>
                    </div>

                    <div className="space-y-3">
                      {o?.deliveryGroups?.map((dg) => {
                        const dgCfg = tripStatusConfig[dg?.status] || tripStatusConfig?.draft;

                        return (
                          <div key={dg?.id} className="bg-white border rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {dg?.number}
                                  </span>
                                  <span
                                    className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${dgCfg.color}`}
                                  >
                                    {dgCfg.label}
                                  </span>
                                  {dg?.shipmentId && (
                                    <span className="text-xs text-muted-foreground border rounded-full px-2 py-0.5 bg-gray-50">
                                      via {dg?.shipmentId}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">{dg.items} items</p>
                              </div>
                              <div className="text-right text-xs">
                                <p className="text-muted-foreground">ETA</p>
                                <p className="font-medium text-gray-900">{dg?.estimatedDelivery}</p>
                              </div>
                            </div>

                            <div className="mt-2">
                              <div className="flex items-center gap-1">
                                {["assigned", "dispatched", "in_transit", "at_hub", "out_for_delivery", "delivered"].map(
                                  (st, i) => {
                                    const statuses = ["assigned", "dispatched", "in_transit", "at_hub", "out_for_delivery", "delivered"];
                                    const currentIdx = statuses.indexOf(dg.status);
                                    const isCompleted = i < currentIdx;
                                    const isCurrent = i === currentIdx;
                                    return (
                                      <div key={st} className="flex items-center flex-1">
                                        <div
                                          className={`h-1.5 flex-1 rounded-full ${
                                            isCompleted ? "bg-green-500" :
                                            isCurrent ? "bg-yellow-400" :
                                            dg.status === "delayed" && i <= currentIdx ? "bg-red-400" : "bg-gray-200"
                                          }`}
                                        />
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                              <div className="flex justify-between text-[10px] text-muted-foreground mt-1.5 px-1">
                                {["Assigned", "Dispatched", "Transit", "Hub", "Out", "Delivered"].map((l) => (
                                  <span key={l} className="text-center" style={{ width: "16.66%" }}>
                                    {l}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {dg.currentEvent && (
                              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground bg-blue-50/50 p-2.5 rounded border border-blue-100">
                                <MapPin className="w-3.5 h-3.5 text-blue-500" />
                                <span className="font-medium text-blue-900">{dg.currentEvent.event}</span>
                                <span className="text-blue-300">·</span>
                                <span>{dg.currentEvent.location}</span>
                                <span className="text-blue-300">·</span>
                                <span className="font-mono">{dg.currentEvent.time}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      <div className="flex-shrink-0 bg-white border-t mt-3 pt-3 pb-2">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={5}
          onPageChange={handlePageChange}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          showInfo={false}
          itemName="Orders"
        />
      </div>
    </div>
  );
} */


  // ─── Order Tracking View ───────────────────────────────────────────────────────
function OrderTrackingView() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedOrders, setExpandedOrders] = useState({});
  const [paginatedOrders, setPaginatedOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const encryptedToken = sessionStorage.getItem("token");
        const token = decryptData(encryptedToken);
        
        let url = `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/all-orders-tracking?page=${currentPage}&limit=5&search_order_number=${search}`;
        if (statusFilter !== "all") {
          url += `&status=${statusFilter}`;
        }

        const res = await fetch(url, { 
          headers: { "Content-Type": "application/json", "x-token": token } 
        });
        const result = await res.json();
        
        if (result.success) {
          setPaginatedOrders(result.data);
          if (result.pagination) {
            setTotalPages(result.pagination.totalPages);
            setTotalItems(result.pagination.total);
          }
        }
      } catch (err) {
        console.error("Tracking Fetch Error:", err);
      }
    };
    
    const delay = setTimeout(fetchTracking, 300);
    return () => clearTimeout(delay);
  }, [currentPage, search, statusFilter]);

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const handlePreviousPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  return (
    <div className="flex flex-col h-[calc(100vh-160px)]">
      {/* ── Top Header & Filters ── */}
      <div className="flex-shrink-0 space-y-4 mb-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={5}
          showInfo={true}
          hideControls={true}
          itemName="orders"
        />

        <div className="flex items-center gap-3 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by Order Number..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <select
            className="border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="all">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="In Transit">In Transit</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* ── Scrollable Order Cards List ── */}
      <div className="flex-1 overflow-y-auto pr-2 space-y-3 min-h-0">
        {(!paginatedOrders || paginatedOrders.length === 0) ? (
          <div className="text-center rounded-lg border border-gray-100 py-16 flex flex-col items-center justify-center text-gray-500 mt-4">
            <Package className="w-8 h-8 mb-2 text-gray-400" />
            <p className="text-sm font-medium">No Orders Found</p>
          </div>
        ) : (
          paginatedOrders.map((o) => {
            const { order_info, total_delivery_groups, delivery_groups } = o;
            const isExpanded = expandedOrders[order_info.order_id];

            // Normalize status for UI styling
            const rawStatus = order_info.order_status?.toLowerCase() || "draft";
            const isDelivered = rawStatus.includes("delivered");
            const isInTransit = rawStatus.includes("transit") || rawStatus.includes("dispatched");

            return (
              <Card key={order_info.order_id} className="overflow-hidden shadow-sm border-gray-200 hover:border-blue-200 transition-colors">
                <div
                  className="p-4 bg-background border-b border-border cursor-pointer hover:bg-slate-50/50"
                  onClick={() => toggleOrderExpand(order_info.order_id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${
                          isDelivered ? "bg-green-100" :
                          isInTransit ? "bg-yellow-100" : "bg-gray-100"
                        }`}
                      >
                        <Package className={`w-5 h-5 ${
                            isDelivered ? "text-green-600" :
                            isInTransit ? "text-yellow-600" : "text-gray-500"
                          }`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-1">
                          <span className=" text-base font-semibold">
                            {order_info.order_number}
                          </span>
                          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                            isDelivered ? "bg-green-100 text-green-700" : isInTransit ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"
                          }`}>
                            {order_info.order_status}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1 font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                            {total_delivery_groups} Delivery Group{total_delivery_groups !== 1 ? "s" : ""}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> 
                            Ordered: {formatDate(order_info.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4 mt-2">
                      <button className="p-1.5 hover:bg-muted rounded-md transition-colors text-muted-foreground border">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Delivery Groups Detail View ── */}
                {isExpanded && (
                  <div className="bg-slate-50 p-4 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Route className="w-4 h-4 text-muted-foreground" />
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                        Delivery Group Progress
                      </p>
                    </div>

                    <div className="space-y-4">
                      {delivery_groups?.map((dg) => {
                        const { dg_info, shipment_info, driver_info, route_progress } = dg;
                        const dgDelivered = dg_info.status?.toLowerCase() === "delivered";

                        // Map backend route stops to standard TrackingTimeline events format
                        const mappedEvents = route_progress?.map(stop => ({
                          time: stop.updated_at ? formatDate(stop.updated_at) : "--",
                          location: stop.location || "Location pending",
                          event: `${stop.stop_type} - ${stop.status}`,
                          status: stop.status === "COMPLETED" ? "completed" : stop.status === "PENDING" ? "pending" : "current",
                          isActionable: false
                        })) || [];

                        return (
                          <div key={dg_info.dg_id} className="bg-white border rounded-xl p-4 shadow-sm relative overflow-hidden">
                            {/* DG Header */}
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-bold text-gray-900">
                                    {dg_info.dg_number}
                                  </span>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                    dgDelivered ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                                  }`}>
                                    {dg_info.status}
                                  </span>
                                  {shipment_info && (
                                    <span className="text-xs font-mono text-muted-foreground border rounded bg-gray-50 px-1.5 py-0.5">
                                      Track: {shipment_info.tracking_number}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <MapPin className="w-3 h-3" />
                                  Dest: {dg_info.destination || "N/A"}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-muted-foreground mb-0.5">Estimated Delivery</p>
                                <p className="font-semibold text-sm text-gray-900">
                                  {shipment_info?.estimated_delivery_date ? formatDate(shipment_info.estimated_delivery_date) : "TBD"}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                              {/* Left: Driver/Shipment Summary */}
                              <div className="space-y-3 border-r pr-4">
                                {driver_info ? (
                                  <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100">
                                    <p className="text-[10px] uppercase font-bold text-blue-500 mb-1">Assigned Partner</p>
                                    <p className="text-sm font-semibold text-gray-900">{driver_info.name}</p>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {driver_info.phone}</span>
                                      <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> {driver_info.vehicle || "N/A"}</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-center">
                                    <p className="text-xs text-muted-foreground">Waiting for driver assignment</p>
                                  </div>
                                )}
                              </div>

                              {/* Right: Chronological Timeline */}
                              <div className="pl-2">
                                <p className="text-[10px] uppercase font-bold text-muted-foreground mb-3">Live Tracking</p>
                                {mappedEvents.length > 0 ? (
                                  <TrackingTimeline events={mappedEvents} onUpdateEvent={() => {}} />
                                ) : (
                                  <p className="text-xs text-muted-foreground italic">No tracking updates available yet.</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* ── Bottom Pagination ── */}
      <div className="flex-shrink-0 bg-white border-t mt-3 pt-3 pb-2">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={5}
          onPageChange={handlePageChange}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          showInfo={false}
          itemName="Orders"
        />
      </div>
    </div>
  );
}

const LEG_TYPE_LABELS = {
  direct: "Direct (A → B)",
  supplier_to_hub: "Supplier → Hub",
  hub_to_hub: "Hub → Hub",
  hub_to_delivery: "Hub → Customer",
  hub_to_supplier: "Hub → Supplier (pickup)",
  return_to_hub: "Return → Hub",
  return_to_supplier: "Return → Supplier",
};

const TRIP_COLORS = [
  { color: "bg-blue-500", textColor: "text-blue-600", borderColor: "border-blue-400" },
  { color: "bg-purple-500", textColor: "text-purple-600", borderColor: "border-purple-400" },
  { color: "bg-orange-500", textColor: "text-orange-600", borderColor: "border-orange-400" },
  { color: "bg-teal-500", textColor: "text-teal-600", borderColor: "border-teal-400" },
];

function getNodeStyle(location, route_legs) {
  const asFrom = route_legs.filter((l) => l.from === location);
  const asTo = route_legs.filter((l) => l.to === location);
  const types = [...asFrom.map((l) => l.type), ...asTo.map((l) => l.type)];
  if (types.some((t) => t.includes("hub")))
    return { icon: "🏭", bg: "bg-blue-100 border-blue-300", label: "Hub" };
  if (types.some((t) => t.includes("supplier")))
    return { icon: "📦", bg: "bg-orange-100 border-orange-300", label: "Supplier" };
  if (types.some((t) => t.includes("delivery")))
    return { icon: "📍", bg: "bg-green-100 border-green-300", label: "Customer" };
  return { icon: "🔵", bg: "bg-gray-100 border-gray-300", label: "Location" };
}

// ─── Route Canvas with Distance & Universal Trip Tracking ─────────────────────
/* function getNodeStyle(location, route_legs = []) {
  const safeLegs = route_legs || [];
  const asFrom = safeLegs.filter((l) => l?.from === location);
  const asTo = safeLegs.filter((l) => l?.to === location);
  const types = [...asFrom.map((l) => l?.type), ...asTo.map((l) => l?.type)];
  if (types.some((t) => t && t.includes("hub")))
    return { icon: "🏭", bg: "bg-blue-100 border-blue-300", label: "Hub" };
  if (types.some((t) => t && t.includes("supplier")))
    return { icon: "📦", bg: "bg-orange-100 border-orange-300", label: "Supplier" };
  if (types.some((t) => t && t.includes("delivery")))
    return { icon: "📍", bg: "bg-green-100 border-green-300", label: "Customer" };
  return { icon: "🔵", bg: "bg-gray-100 border-gray-300", label: "Location" };
}  */ 

function RouteCanvas({ legs, trips, routeMode }) {
  if (legs?.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 border-2 border-dashed rounded-xl text-muted-foreground text-sm">
        Add legs below to see the route diagram
      </div>
    );
  }

  if (routeMode === "multi_trip" && trips.length > 1) {
    return (
      <div className="space-y-2 p-4 bg-muted/20 rounded-xl border">
        {trips.map((trip, ti) => {
          const tripLegs = legs.filter((l) => trip.legIds.includes(l.id));
          if (tripLegs.length === 0) return null;

          const waypoints = [];
          tripLegs.forEach((l) => {
            if (!waypoints.includes(l.from)) waypoints.push(l.from);
            if (!waypoints.includes(l.to)) waypoints.push(l.to);
          });

          return (
            <div key={trip.id}>
              {trip.linkedFromTripId && trip.handoffPoint && (
                <div className="flex items-center gap-2 pl-3 py-1 mb-1">
                  <div className="w-0.5 h-5 bg-dashed border-l-2 border-dashed border-gray-400 ml-5" />
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-yellow-50 border border-yellow-200 rounded-full px-3 py-1">
                    <ArrowRight className="w-3 h-3 text-yellow-600" />
                    <span className="font-medium text-yellow-700">Handoff at {trip.handoffPoint || "shared point"}</span>
                    <span>— Trip {trips.findIndex((t) => t.id === trip.linkedFromTripId) + 1} delivers here first</span>
                  </div>
                </div>
              )}

              <div className={`flex items-center gap-0 p-3 rounded-lg border-2 ${trip.borderColor} bg-white`}>
                <div className={`text-xs font-bold px-2 py-1 rounded-full ${trip.color} text-white mr-3 flex-shrink-0`}>
                  T{ti + 1}
                </div>
                {waypoints.map((wp, wi) => {
                  const ns = getNodeStyle(wp, tripLegs);
                  const leg = tripLegs[wi];
                  return (
                    <div key={wp + wi} className="flex items-center gap-0 min-w-0">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-10 h-10 rounded-lg border-2 ${ns.bg} flex items-center justify-center text-lg`}>{ns.icon}</div>
                        <p className="text-xs text-muted-foreground mt-1 max-w-[80px] text-center truncate">{wp || "—"}</p>
                      </div>
                      {wi < waypoints.length - 1 && leg && (
                        <div className={`flex flex-col items-center mx-2 flex-shrink-0 ${leg.isReturn ? "opacity-60" : ""}`}>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${leg.isReturn ? "border-dashed border-gray-300 bg-gray-50 text-gray-500" : `${trip.borderColor} bg-white ${trip.textColor}`}`}>
                            {leg.isReturn ? "↩" : "→"} {leg.carrier ? leg.carrier.split(" ")[0] : ""}
                          </div>
                          
                          {/* ADDED: Distance Display for Multi-Trip Mode */}
                          {leg.distance && (
                            <span className="text-[10px] font-bold text-blue-600 mt-1">
                              📏 {leg.distance} km
                            </span>
                          )}

                          {leg.arrivalPlanned && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              ETA {leg.arrivalPlanned.split("T")[0]}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const waypoints = [];
  legs?.forEach((l) => {
    if (!waypoints.includes(l.from)) waypoints.push(l.from);
    if (!waypoints.includes(l.to)) waypoints.push(l.to);
  });
  const isRound = routeMode === "round_trip" && waypoints.length > 1;

  return (
    <div className="p-4 bg-muted/20 rounded-xl border overflow-x-auto">
      <div className="flex items-start gap-0 min-w-max">
        {waypoints.map((wp, wi) => {
          const ns = getNodeStyle(wp, legs);
          const leg = legs[wi];
          const trip = trips.find((t) => leg && t.legIds.includes(leg.id));
          return (
            <div key={wp + wi} className="flex items-start gap-0">
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-11 h-11 rounded-lg border-2 ${ns.bg} flex items-center justify-center text-xl`}>{ns.icon}</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-[88px] text-center leading-tight">{wp || "—"}</p>
              </div>
              {wi < waypoints.length - 1 && leg && (
                <div className="flex flex-col items-center mx-3 mt-3 flex-shrink-0">
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${leg.isReturn
                    ? "border-dashed border-gray-300 bg-gray-50 text-gray-500"
                    : trip
                    ? `${trip.borderColor} bg-white ${trip.textColor}`
                    : "border-border bg-white text-muted-foreground"}`}>
                    {leg.isReturn ? "↩ return" : "→"} {leg.carrier ? leg.carrier.split(" ")[0] : ""}
                  </div>
                  
                  {/* ADDED: Distance Display for Linear/Round Trip Mode */}
                  {leg.distance && (
                    <span className="text-[10px] font-bold text-blue-600 mt-1">
                      📏 {leg.distance} km
                    </span>
                  )}

                  {leg.arrivalPlanned && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 text-center">
                      {leg.arrivalPlanned.split("T")[0]}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {isRound && (
          <div className="flex flex-col items-center ml-2 mt-3 flex-shrink-0">
            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-dashed border-indigo-300 bg-indigo-50 text-indigo-600">
              ↩ returns to origin
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* function RouteCanvas({ route_legs = [], trips = [], routeMode = "linear" }) {
  const safeLegs = route_legs || [];
  const safeTrips = trips || [];

  if (safeLegs.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 border-2 border-dashed rounded-xl text-muted-foreground text-sm">
        Add legs below to see the route diagram and distances
      </div>
    );
  }

  if (routeMode === "multi_trip" && safeTrips.length > 1) {
    return (
      <div className="space-y-2 p-4 bg-muted/20 rounded-xl border">
        {safeTrips.map((trip, ti) => {
          const tripLegs = safeLegs.filter((l) => trip?.legIds?.includes(l?.id));
          if (tripLegs.length === 0) return null;

          const waypoints = [];
          tripLegs.forEach((l) => {
            if (l?.from && !waypoints.includes(l.from)) waypoints.push(l.from);
            if (l?.to && !waypoints.includes(l.to)) waypoints.push(l.to);
          });

          return (
            <div key={trip?.id || ti}>
              {trip?.linkedFromTripId && trip?.handoffPoint && (
                <div className="flex items-center gap-2 pl-3 py-1 mb-1">
                  <div className="w-0.5 h-5 border-l-2 border-dashed border-gray-400 ml-5" />
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-yellow-50 border border-yellow-200 rounded-full px-3 py-1">
                    <ArrowRight className="w-3 h-3 text-yellow-600" />
                    <span className="font-medium text-yellow-700">Handoff at {trip.handoffPoint}</span>
                  </div>
                </div>
              )}

              <div className={`flex items-center gap-0 p-3 rounded-lg border-2 ${trip?.borderColor || 'border-gray-200'} bg-white overflow-x-auto`}>
                <div className={`text-xs font-bold px-2 py-1 rounded-full ${trip?.color || 'bg-blue-500'} text-white mr-3 flex-shrink-0`}>
                  T{ti + 1}
                </div>
                {waypoints.map((wp, wi) => {
                  const ns = getNodeStyle(wp, tripLegs);
                  const leg = tripLegs[wi];
                  return (
                    <div key={wp + wi} className="flex items-center gap-0 min-w-0">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-10 h-10 rounded-lg border-2 ${ns.bg} flex items-center justify-center text-lg`}>{ns.icon}</div>
                        <p className="text-xs text-muted-foreground mt-1 max-w-[80px] text-center truncate">{wp || "—"}</p>
                      </div>
                      {wi < waypoints.length - 1 && leg && (
                        <div className={`flex flex-col items-center mx-2 flex-shrink-0 ${leg.isReturn ? "opacity-60" : ""}`}>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${leg.isReturn ? "border-dashed border-gray-300 bg-gray-50 text-gray-500" : `${trip?.borderColor} bg-white ${trip?.textColor}`}`}>
                            {leg.isReturn ? "↩" : "→"} {leg.carrier ? leg.carrier.split(" ")[0] : ""}
                          </div>
                          <span className="text-[10px] font-semibold text-blue-600 mt-0.5">{leg.distance || "120 km"}</span>
                          {leg.arrivalPlanned && (<p className="text-[10px] text-muted-foreground">ETA {leg.arrivalPlanned.split("T")[0]}</p>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const waypoints = [];
  safeLegs.forEach((l) => {
    if (l?.from && !waypoints.includes(l.from)) waypoints.push(l.from);
    if (l?.to && !waypoints.includes(l.to)) waypoints.push(l.to);
  });

  return (
    <div className="p-4 bg-muted/20 rounded-xl border overflow-x-auto">
      <div className="flex items-start gap-0 min-w-max">
        {waypoints.map((wp, wi) => {
          const ns = getNodeStyle(wp, safeLegs);
          const leg = safeLegs[wi];
          const trip = safeTrips.find((t) => leg && t?.legIds?.includes(leg.id));
          return (
            <div key={wp + wi} className="flex items-start gap-0">
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-11 h-11 rounded-lg border-2 ${ns.bg} flex items-center justify-center text-xl`}>{ns.icon}</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-[88px] text-center leading-tight">{wp || "—"}</p>
              </div>
              {wi < waypoints.length - 1 && leg && (
                <div className="flex flex-col items-center mx-3 mt-3 flex-shrink-0">
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
                    leg.isReturn
                      ? "border-dashed border-gray-300 bg-gray-50 text-gray-500"
                      : trip
                      ? `${trip.borderColor} bg-white ${trip.textColor}`
                      : "border-border bg-white text-muted-foreground"
                  }`}>
                    {leg.isReturn ? "↩ return" : "→"} {leg.carrier ? leg.carrier.split(" ")[0] : "Direct"}
                  </div>
                  <span className="text-[10px] font-bold text-blue-600 mt-1">📏 {leg.distance || "85 km"}</span>
                  {leg.arrivalPlanned && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 text-center">
                      {leg.arrivalPlanned.split("T")[0]}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}  */ 


export function TripEtaDisplay({ trip }) {
  const [timeState, setTimeState] = useState({ diff: 0, label: "", isDelayed: false });
  const priority = trip.priority === "high" ? "High" : "Normal";

  useEffect(() => {
    if (!trip.estimated_delivery_time) return;

    const interval = setInterval(() => {
      const now = new Date();
      const eta = new Date(trip.estimated_delivery_time);
      const completionTime = trip.actual_delivery_time ? new Date(trip.actual_delivery_time) : null;
      
      const timeToCompare = completionTime || now;
      const diffMs = eta - timeToCompare; 
      const diffMins = Math.round(diffMs / 60000);

      if (diffMins < 0) {
        setTimeState({ diff: Math.abs(diffMins), label: "Delayed by", isDelayed: true });
      } else {
        setTimeState({ diff: diffMins, label: completionTime ? "Finished ahead by" : "Ahead by", isDelayed: false });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [trip]);

  return (
    <div className="flex flex-col gap-1 text-xs mt-2">
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
          {priority}
        </span>
        {trip.totalDistance && <span className="text-muted-foreground font-medium bg-gray-100 px-2 py-0.5 rounded">Total: {trip.totalDistance} km</span>}
      </div>
      
      {trip.estimated_delivery_time && (
        <div className={`flex items-center gap-1.5 font-medium ${timeState.isDelayed ? 'text-red-600' : 'text-green-600'}`}>
          {timeState.isDelayed ? <AlertCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
          <span>
            {timeState.label} {timeState.diff} mins 
            {trip.totalEstimatedMinutes && ` (${trip.totalEstimatedMinutes} min est. total)`}
          </span>
        </div>
      )}
    </div>
  );
}

function CreateTripWizard({ onClose, onCreated, partners, vehicles }) {
  console.log(partners)
  console.log(vehicles)
  const [step, setStep] = useState(1);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [regionFilter, setRegionFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [tripName, setTripName] = useState("");
  const [tripType, setTripType] = useState("direct");
  const [priority, setPriority] = useState("medium");
  const [origin, setOrigin] = useState("");
  const [estimatedDelivery, setEstimatedDelivery] = useState("");
  const [notes, setNotes] = useState("");
  const [routeMode, setRouteMode] = useState("linear");
  const [legs, setLegs] = useState([
    { id: "l1", sequence: 1, type: "direct", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1",po_id: "", dg_id: "" },
  ]);
  const [trips, setTrips] = useState([
    { id: "trip-1", label: "Trip 1", ...TRIP_COLORS[0], legIds: ["l1"] },
  ]);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("direct");

  const [originCompany, setOriginCompany] = useState("");
  const [originAddress, setOriginAddress] = useState("");
  const [originCity, setOriginCity] = useState("");
  const [originState, setOriginState] = useState("");
  const [pickupDate, setPickupDate] = useState("");

  const [destCompany, setDestCompany] = useState("");
  const [destAddress, setDestAddress] = useState("");
  const [destCity, setDestCity] = useState("");
  const [destState, setDestState] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [manualWeight, setManualWeight] = useState("");
  const [manualValue, setManualValue] = useState("");
    // Tracks the individual shipments added to this Trip
  const [sessionShipments, setSessionShipments] = useState([]);
  const [deliverytrips, setDeliverytrips] = useState([]);
  const [filteredDeliverytrips, setFilteredDeliverytrips] = useState([]);
  const [expandedDeliveryGroups, setExpandedDeliveryGroups] = useState({});
  // Tracks selections specifically for the current shipment being built in Step 2
  const [step2SelectedItems, setStep2SelectedItems] = useState([]);
  const [activeAssignTripId, setActiveAssignTripId] = useState(null);
  const [showPopupMessages, setShowPopupMessages] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('');
  const [selectedRiders, setSelectedRiders] = useState([]);

// 1. Memoize step2CartItems to prevent new array references on every render
const step2CartItems = useMemo(() => {
    return selectedGroups.flatMap(g => {
      const order = filteredDeliverytrips.find(o => o.id === g.orderId);
      if (!order) return [];
      
      if (g.itemIds.length === 0) {
        const firstDg = order.delivery_groups?.[0];
        const poId = firstDg?.purchaseorder_id || firstDg?.sub_orders?.[0]?.purchaseorder_id || "";
        return [{
          id: firstDg?._id || order.id,
          orderId: order.id,
          po_id: poId,
          label: order.order_number,
          customer: order.customer_name,
          city: order.city,
          state: order.state,
          value: order.total || 0,
          type: "Order"
        }];
      } else {
        return g.itemIds.map(dgId => {
          const dg = order.delivery_groups?.find(d => d._id === dgId);
          if (!dg) return null;
          const dgValue = dg.itemsData?.reduce((acc, it) => acc + (it.finalPrice || 0), 0) || 0;
          const poId = dg.purchaseorder_id || dg.sub_orders?.[0]?.purchaseorder_id || "";
          return {
            id: dg._id,
            orderId: order.id,
            po_id: poId,
            label: `${order.order_number} - ${dg.delivery_group_number}`,
            customer: order.customer_name,
            city: order.city,
            state: order.state,
            value: dgValue,
            type: "Delivery Group"
          };
        }).filter(Boolean);
      }
    });
  }, [selectedGroups, filteredDeliverytrips]);

  // 2. Create a stable string dependency to completely prevent infinite loops
  const step2ItemIdsString = step2CartItems.map(item => item.id).join(',');

  // 3. Auto-generate shipments using the stable string dependency
  useEffect(() => {
    if (step2CartItems && step2CartItems.length > 0) {
      const autoGeneratedShipments = step2CartItems.map((item, index) => {
        // Find the full delivery group to extract Origin data if available
        const order = filteredDeliverytrips.find(o => o.id === item.orderId);
        const dg = order?.delivery_groups?.find(d => d._id === item.id);

        console.log("Delivery Group:", dg);
        
        const originName = dg?.supplier && dg.supplier !== "--" ? dg.supplier : "Supplier Hub";
        const destName = `${item.city || ""}, ${item.state || ""}`.replace(/(^,\s*)|(,\s*$)/g, "") || item.customer;

        return {
          // Base the ID on the item ID so it stays stable across renders
          id: `SHP-AUTO-${item.id.slice(-5)}`, 
          origin: originName,
          destination: destName,
          weight: 0, 
          value: item.value || 0,
          assignedItemIds: [item.id], 
          orders: [{
            id: item.orderId,
            customer: item.customer,
            label: item.label
          }],
          pickupDate: "",
          deliveryDate: "",
          po_id: dg?.sub_orders?.purchaseorder_id || "",
          dg_id: dg?._id || "",
          notes: "",
          status: "pending"
        };
      });

      // Update the shipment state automatically
      setSessionShipments(autoGeneratedShipments);
      
      // Automatically mark them as selected in the Step 2 UI
      setStep2SelectedItems(step2CartItems.map(item => item.id));
    } else {
      // Clear auto-shipments if selections are removed
      setSessionShipments([]);
      setStep2SelectedItems([]);
    }
  // ONLY trigger if the string of selected IDs physically changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step2ItemIdsString, filteredDeliverytrips]);

  const toggleStep2Item = (id) => {
    setStep2SelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

    useEffect(() => {
      fetchDeliveryGroupsForTrips();
    }, []);

  const toggleGroupExpand = (e, groupId) => {
  e.stopPropagation(); // Prevents the group selection checkbox from firing when clicking the chevron
  setExpandedDeliveryGroups((prev) => ({
    ...prev,
    [groupId]: !prev[groupId],
  }));
};  

  const status = [...new Set(filteredDeliverytrips.map((o) => o.status))];
const filteredAvailable = filteredDeliverytrips.filter((o) => {
    const matchStatus = statusFilter === "all" || o.status === statusFilter;
    const matchSearch =
      orderSearch === "" ||
      o.id?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.customer_name?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.city?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.state?.toLowerCase().includes(orderSearch.toLowerCase());
      
    return matchStatus && matchSearch;
  });

  // --- ADD THIS FOR PAGINATION ---
  const {
    currentItems: paginatedOrders,
    currentPage: step1CurrentPage,
    totalPages: step1TotalPages,
    totalItems: step1TotalItems,
    handlePageChange: handleStep1PageChange,
    handlePreviousPage: handleStep1PreviousPage,
    handleNextPage: handleStep1NextPage,
    //resetToFirstPage: resetStep1Page,
  } = usePagination(filteredAvailable, 5);

  const handleOrderSearchChange = (e) => {
    setOrderSearch(e.target.value);
    //resetStep1Page(); // Reset to page 1 when searching
  };
  // -------------------------------

  const isOrderSelected = (orderId) => selectedGroups.some((g) => g.orderId === orderId && g.itemIds.length === 0);
  const isItemSelected = (orderId, itemId) => selectedGroups.some((g) => g.orderId === orderId && (g.itemIds.length === 0 || g.itemIds.includes(itemId)));

  const toggleOrder = (orderId) => {
    if (isOrderSelected(orderId)) {
      setSelectedGroups((p) => p.filter((g) => g.orderId !== orderId));
    } else {
      setSelectedGroups((p) => [
        ...p.filter((g) => g.orderId !== orderId),
        { orderId, itemIds: [] },
      ]);
    }
  };

  const toggleItem = (orderId, itemId, allItemIds) => {
    const existing = selectedGroups.find((g) => g.orderId === orderId);
    if (!existing) {
      setSelectedGroups((p) => [...p, { orderId, itemIds: [itemId] }]);
      return;
    }
    if (existing.itemIds.length === 0) {
      const newIds = allItemIds.filter((id) => id !== itemId);
      setSelectedGroups((p) => [
        ...p.filter((g) => g.orderId !== orderId),
        ...(newIds.length > 0 ? [{ orderId, itemIds: newIds }] : []),
      ]);
      return;
    }
    const has = existing.itemIds.includes(itemId);
    const newIds = has ? existing.itemIds.filter((id) => id !== itemId) : [...existing.itemIds, itemId];
    if (newIds.length === allItemIds.length) {
      setSelectedGroups((p) => [
        ...p.filter((g) => g.orderId !== orderId),
        { orderId, itemIds: [] },
      ]);
    } else {
      setSelectedGroups((p) => [
        ...p.filter((g) => g.orderId !== orderId),
        ...(newIds.length > 0 ? [{ orderId, itemIds: newIds }] : []),
      ]);
    }
  };

  const autoGroupByRegion = () => {
    if (regionFilter !== "all") {
      const regional = filteredDeliverytrips.filter((o) => o.region === regionFilter);
      const toAdd = regional.filter((o) => o.readyToShip).map((o) => ({ orderId: o.id, itemIds: [] }));
      const existing = selectedGroups.filter((g) => !toAdd.some((a) => a.orderId === g.orderId));
      setSelectedGroups([...existing, ...toAdd]);
      toast.success(`Auto-grouped ${toAdd.length} ready orders from ${regionFilter}`);
    } else {
      toast.info("Select a region first to auto-group");
    }
  };

  const selectedOrderDetails = filteredDeliverytrips.filter((o) => selectedGroups.some((g) => g.orderId === o.id));
  const totalSelectedWeight = selectedGroups.reduce((sum, g) => {
    const order = filteredDeliverytrips.find((o) => o.id === g.orderId);
    if (!order) return sum;
    if (g.itemIds.length === 0) {
      return sum + parseFloat(order?.totalWeight?.replace(/[^0-9.]/g, ""));
    }
    return sum + g.itemIds.reduce((s, id) => {
      const item = order?.delivery_groups?.find((i) => i._id === id);
      return s + parseFloat((item?.weight ?? "0").replace(/[^0-9.]/g, ""));
    }, 0);
  }, 0);

  const totalSelectedValue = selectedGroups.reduce((sum, g) => {
    const order = filteredDeliverytrips.find((o) => o.id === g.orderId);
    return sum + (order?.totalValue ?? 0);
  }, 0);

  const canProceedStep1 = selectedGroups.length > 0;
  const canProceedStep2 = legs.length > 0;
  const canProceedStep3 = trips.every((t) => t.partnerId);;

  const suggestedName = selectedOrderDetails.length > 0
    ? selectedOrderDetails.length === 1
      ? `Delivery — ${selectedOrderDetails[0].customer_name}`
      : `Batch Delivery — ${selectedOrderDetails.slice(0, 2).map((o) => o.customer_name.split(" ")[0]).join(" & ")}${selectedOrderDetails.length > 2 ? ` +${selectedOrderDetails.length - 2}` : ""}`
    : "";

  // Extract unique locations (Customers, Suppliers, Hubs) from selected orders
const availableLocations = useMemo(() => {
  const locations = [];
  const added = new Set();

  const addLoc = (loc) => {
    if (!added.has(loc.id)) {
      locations.push(loc);
      added.add(loc.id);
    }
  };

  selectedOrderDetails.forEach((order) => {
    const orderId = order._id || order.id;

    // 1. Extract Customer (using lat/lng mapped to the order root)
    if (order.customer_name) {
      addLoc({
        id: `cust-${orderId}`,
        type: "customer",
        name: order.customer_name,
        address: `${order.city || ""}, ${order.state || ""}`.replace(/(^,\s*)|(,\s*$)/g, ""),
        city: order.city || "",
        state: order.state || "",
        lat: order.lat || null,
        lng: order.lng || null,
        label: `${order.customer_name} (Customer)`
      });
    }

    order.delivery_groups?.forEach((dg) => {
      // 2. Extract Supplier (using the flattened dg properties)
      if (dg.supplier && dg.supplier !== "--") {
        addLoc({
          id: `sup-${dg._id}`,
          type: "supplier",
          name: dg.supplier,
          address: dg.location && dg.location !== "--" ? dg.location : "",
          city: dg.city || "",
          state: dg.state || "",
          lat: dg.lat || null,
          lng: dg.lng || null,
          label: `${dg.supplier} (Supplier)`
        });
      }
    });

    // 3. Extract Hub (requires adding hub mapping to fetchDeliveryGroupsForTrips)
    if (order.hub_name) {
      addLoc({
        id: `hub-${orderId}`,
        type: "hub",
        name: order.hub_name,
        address: order.hub_address || "",
        city: order.hub_city || "", 
        state: order.hub_state || "",
        lat: null,
        lng: null,
        label: `${order.hub_name} (Hub)`
      });
    }
  });

  // Fallback: Ensure a generic Central Hub is always available if none map correctly
  if (locations.filter(l => l.type === 'hub').length === 0) {
    addLoc({
      id: 'default-hub', type: 'hub', name: 'Central Hub', address: 'Main Facility', city: '', state: '', lat: null, lng: null, label: 'Central Hub (Hub)'
    });
  }

  return locations;
}, [selectedOrderDetails]);

  const nextLegId = () => `l${Date.now()}`;
  const addLeg = (tripId = "trip-1", type = "direct") => {
    const id = nextLegId();
    setLegs((p) => {
      const newSeq = p.length + 1;
      return [...p, { id, sequence: newSeq, type, from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId }];
    });
    setTrips((p) => p.map((t) => (t.id === tripId ? { ...t, legIds: [...t.legIds, id] } : t)));
  };

  const removeLeg = (id) => {
    setLegs((p) => p.filter((l) => l.id !== id).map((l, i) => ({ ...l, sequence: i + 1 })));
    setTrips((p) => p.map((t) => ({ ...t, legIds: t.legIds.filter((lid) => lid !== id) })));
  };

  //const updateLeg = (id, field, value) => setLegs((p) => p.map((l) => (l.id === id ? { ...l, [field]: value } : l)));

const updateLeg = async (id, field, value) => {
  // First, update the immediate UI state
  setLegs((prev) => prev.map(l => l.id === id ? { ...l, [field]: value } : l));

  // If locations or departure times change, recalculate via Google Routes API
  if (['from', 'to', 'departurePlanned'].includes(field)) {
    const currentLegs = [...legs];
    const legIndex = currentLegs.findIndex(l => l.id === id);
    const updatedLeg = { ...currentLegs[legIndex], [field]: value };

    const fromLoc = availableLocations.find(loc => loc.name === updatedLeg.from);
    const toLoc = availableLocations.find(loc => loc.name === updatedLeg.to);

    if (fromLoc?.lat && fromLoc?.lng && toLoc?.lat && toLoc?.lng) {
      const routeData = await fetchRouteComputation(fromLoc, toLoc);
      
      if (routeData) {
        setLegs(prev => prev.map(l => {
          if (l.id !== id) return l;
          const newLeg = { ...l, distance: parseFloat(routeData.distanceKm || routeData.totalDistanceKm).toFixed(1) };
          
          if (newLeg.departurePlanned && (routeData.durationMinutes || routeData.totalDurationMinutes)) {
            newLeg.arrivalPlanned = addMinutesToDateTimeLocal(
              newLeg.departurePlanned, 
              routeData.durationMinutes || routeData.totalDurationMinutes
            );
          }
          return newLeg;
        }));
      } else {
        // Fallback to Haversine if API fails
        const dist = calculateDistance(fromLoc.lat, fromLoc.lng, toLoc.lat, toLoc.lng);
        setLegs(prev => prev.map(l => l.id === id ? { 
          ...l, 
          distance: dist, 
          arrivalPlanned: l.departurePlanned ? addMinutesToDateTimeLocal(l.departurePlanned, Math.round(dist * 1.5)) : l.arrivalPlanned 
        } : l));
      }
    }
  }
};

  const addReturnLeg = (tripId) => {
    const tripLegs = legs.filter((l) => l.tripId === tripId);
    const lastLeg = tripLegs[tripLegs.length - 1];
    if (!lastLeg) {
      addLeg(tripId, "return_to_hub");
      return;
    }
    const id = nextLegId();
    setLegs((p) => [
      ...p,
      {
        id,
        sequence: p.length + 1,
        type: "return_to_hub",
        from: lastLeg.to,
        to: lastLeg.from.includes("Hub") || lastLeg.from.includes("hub") ? lastLeg.from : lastLeg.from || "",
        departurePlanned: "",
        arrivalPlanned: "",
        carrier: lastLeg.carrier,
        tripId,
        isReturn: true,
      },
    ]);
    setTrips((p) => p.map((t) => (t.id === tripId ? { ...t, legIds: [...t.legIds, id] } : t)));
  };

  const addLinkedTrip = () => {
    const newTripIdx = trips.length;
    const colorSet = TRIP_COLORS[newTripIdx % TRIP_COLORS.length];
    const prevTrip = trips[trips.length - 1];
    const prevTripLegs = legs.filter((l) => prevTrip.legIds.includes(l.id));
    const handoffPoint = prevTripLegs[prevTripLegs.length - 1]?.to || "";
    const newTripId = `trip-${newTripIdx + 1}`;
    const newLegId = nextLegId();
    setTrips((p) => [
      ...p,
      { id: newTripId, label: `Trip ${newTripIdx + 1}`, ...colorSet, legIds: [newLegId], linkedFromTripId: prevTrip.id, handoffPoint },
    ]);
    setLegs((p) => [
      ...p,
      {
        id: newLegId,
        sequence: p.length + 1,
        type: "hub_to_delivery",
        from: handoffPoint,
        to: "",
        departurePlanned: "",
        arrivalPlanned: "",
        carrier: "",
        tripId: newTripId,
      },
    ]);
  };

  const updateTrip = (tripId, field, value) => setTrips((p) => p.map((t) => (t.id === tripId ? { ...t, [field]: value } : t)));

  const removeTrip = (tripId) => {
    if (trips.length <= 1) return;
    const tripLegIds = trips.find((t) => t.id === tripId)?.legIds || [];
    setLegs((p) => p.filter((l) => !tripLegIds.includes(l.id)).map((l, i) => ({ ...l, sequence: i + 1 })));
    setTrips((p) => p.filter((t) => t.id !== tripId).map((t) => ({
      ...t,
      linkedFromTripId: t.linkedFromTripId === tripId ? undefined : t.linkedFromTripId,
    })));
  };

  const applyTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    const id1 = nextLegId(), id2 = `${nextLegId()}b`, id3 = `${nextLegId()}c`, id4 = `${nextLegId()}d`;
    if (tpl === "direct") {
      setRouteMode("linear");
      setTripType("direct");
      setLegs([{ id: id1, sequence: 1, type: "direct", from: origin, to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1" }]);
      setTrips([{ id: "trip-1", label: "Trip 1", ...TRIP_COLORS[0], legIds: [id1] }]);
    } else if (tpl === "hub_spoke") {
      setRouteMode("linear");
      setTripType("hub_and_spoke");
      setLegs([
        { id: id1, sequence: 1, type: "supplier_to_hub", from: origin, to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1" },
        { id: id2, sequence: 2, type: "hub_to_delivery", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1" },
      ]);
      setTrips([{ id: "trip-1", label: "Trip 1", ...TRIP_COLORS[0], legIds: [id1, id2] }]);
    } else if (tpl === "round_trip_full") {
      setRouteMode("round_trip");
      setTripType("hub_and_spoke");
      setLegs([
        { id: id1, sequence: 1, type: "hub_to_supplier", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1" },
        { id: id2, sequence: 2, type: "supplier_to_hub", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1", isReturn: false },
        { id: id3, sequence: 3, type: "hub_to_delivery", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1" },
        { id: id4, sequence: 4, type: "return_to_hub", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1", isReturn: true },
      ]);
      setTrips([{ id: "trip-1", label: "Round Trip", ...TRIP_COLORS[0], legIds: [id1, id2, id3, id4] }]);
    } else if (tpl === "multi_trip_linked") {
      setRouteMode("multi_trip");
      setTripType("hub_and_spoke");
      setLegs([
        { id: id1, sequence: 1, type: "supplier_to_hub", from: origin, to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-1" },
        { id: id2, sequence: 2, type: "hub_to_delivery", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "", tripId: "trip-2" },
      ]);
      setTrips([
        { id: "trip-1", label: "Trip 1 — Inbound", ...TRIP_COLORS[0], legIds: [id1] },
        { id: "trip-2", label: "Trip 2 — Outbound", ...TRIP_COLORS[1], legIds: [id2], linkedFromTripId: "trip-1", handoffPoint: "" },
      ]);
    }
  };

  const toggleRiderSelection = (riderId) => {
    setSelectedRiders((prev) =>
      prev.includes(riderId) ? prev.filter((id) => id !== riderId) : [...prev, riderId]
    );
  };

const fetchDeliveryGroupsForTrips = async () => {
    setLoading(true);
    try {
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}customer/salesorder/get-deliverygroups-for-shipment`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
        }
      );

      const resData = await response.json();
      const orders = resData?.data || [];
      console.log("deliverygroups:", orders);

      /* ✅ MAP ORDER → DELIVERY GROUPS */
      const mappedData = orders.map((order) => ({
        id: order._id,          // <--- ADD THIS: Required for selection/search
        _id: order._id,         // <--- ADD THIS: Required for toggleOrder
        order_id: order._id,
        order_number: order.order_number,
        customer_name: order.customer_name,
        customer_mobile: order.customer_mobile,
        customer_type: order.customer_type,
        hub_name: order.hub_name,
        hub_address: order.hub_address,
        hub_city: order.hub_city,
        hub_state: order.hub_state,
        delivery_eta: order.delivery_eta || "--",
        distance: order.distance || "N/A",
        lat: order.location?.lat || null,
        lng: order.location?.lng || null,
        city: order.city,
        state: order.state,
        total: order.total,
        salesorder_date: order.salesorder_date,
        createdAt: order.delivery_groups?.[0]?.created_at
          ? new Date(order.delivery_groups[0].created_at).toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "--",

        createdAtRaw: order.delivery_groups?.[0]?.created_at || null,

        delivery_groups: (order.delivery_groups || []).map((dg) => {
          const vendor = dg.sub_orders?.[0]?.vendor_id;
          const purchaseOrderId = dg.sub_orders?.[0]?.purchaseorder_id || null;

          return {
            _id: dg._id,
            purchaseorder_id: purchaseOrderId,
            delivery_group_number: dg.delivery_group_number,
            deliveredBy: dg.delivered_to,
            delivery_charges: dg.delivery_charges,
            delivery_group_status: dg.delivery_group_status,
            distance: dg.distance || "N/A",
            delivery_date: dg.delivery_date
              ? new Date(dg.delivery_date).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "--",
            delivery_slot: dg.delivery_slot,
            itemsData: dg.sub_orders?.flatMap((so) =>
              (so.items || []).map((item) => ({
                name: item.item_name,
                finalPrice: item.item_total_price,
                awardedTo: item.awarded_to,
                image: item.item_image,
              }))
            ),

            supplier: vendor?.name || "--",
            city: vendor?.city || "",
            state: vendor?.state || "",
            location: vendor?.address || "--",
            lat: vendor?.location?.lat || null,
            lng: vendor?.location?.lng || null,
            vehicleCharge: dg.delivery_vehicle_type || "--",
            subOrderStatus: dg.sub_orders?.[0]?.status || "--",
            billingStatus: dg.sub_orders?.[0]?.billing_status || "--",

            shipmentStatus: dg.delivery_shipments?.[0]?.status || "--",
            shipmentId: dg.delivery_shipments?.[0]?._id || null,
            shipmentNo: dg.delivery_shipments?.[0]?.delivery_shipment_number || "-",
          };
        }),
      }));

      setDeliverytrips(mappedData);
      setFilteredDeliverytrips(mappedData);
    } catch (error) {
      console.error("Error fetching deliverygroupsfortrips:", error);
    } finally {
      setLoading(false);
    }
  };


// 1. Adds a Shipment to the current Trip session
const handleAddShipment = () => {
    const selectedCartItems = step2CartItems.filter(i => step2SelectedItems.includes(i.id));
    const hasItems = selectedCartItems.length > 0;
    
    const originName = [originCity, originState].filter(Boolean).join(", ") || originCompany || origin;
    const destName = [destCity, destState].filter(Boolean).join(", ") || destCompany;

    if (!originName || !destName) {
      toast.error("Please provide at least Origin and Destination details.");
      return;
    }

    if (!hasItems && !manualWeight && !manualValue) {
      toast.error("Please select items for this shipment or provide manual weight/value.");
      return;
    }

    const dests = [...new Set(selectedCartItems.map(c => `${c.city}, ${c.state}`))];
    const finalDestination = hasItems ? dests.join(" / ") : destName;
    const finalValue = hasItems ? selectedCartItems.reduce((acc, curr) => acc + curr.value, 0) : parseFloat(manualValue || 0);
    const finalWeight = parseFloat(manualWeight || 0); 

    const newShipment = {
      id: `SHP-${Date.now().toString().slice(-5)}`,
      origin: originName || "Manual Origin",
      destination: finalDestination,
      weight: finalWeight,
      value: finalValue,
      assignedItemIds: step2SelectedItems,
      orders: selectedCartItems.map(c => ({
        id: c.orderId,
        customer: c.customer,
        label: c.label
      })),
      pickupDate,
      deliveryDate,
      po_id: selectedCartItems[0]?.po_id || "",
      dg_id: selectedCartItems[0]?.id || "",
      notes: notes.trim() || "",
      status: "pending"
    };

    setSessionShipments((prev) => [...prev, newShipment]);
    toast.success("Shipment added to Trip plan");

    setStep2SelectedItems([]);
    setOriginCompany("");
    setOriginAddress("");
    setOriginCity("");
    setOriginState("");
    setOrigin("");
    setDestCompany("");
    setDestAddress("");
    setDestCity("");
    setDestState("");
    setManualWeight("");
    setManualValue("");
  };

// 2. Final submission: Updates existing trip(s) if an ID is present, or creates new ones cleanly
/* const handleCreateTrip = async () => {
    try {
      setLoading(true);
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const createdShipmentIds = [];

      // 1. CREATE / UPDATE SHIPMENTS
      for (const shipment of sessionShipments) {
        console.log("Processing shipment:", shipment);
        const shipmentPayload = {
          dg_ids: shipment.assignedItemIds,
          origin: { address: shipment.origin },
          destination: { address: shipment.destination },
          weight: shipment.weight,
          status: "Pending"
        };

        const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-shipment`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-token": token },
          body: JSON.stringify(shipmentPayload)
        });
        const result = await res.json();
        if (result.success && result.data) {
          createdShipmentIds.push(result.data._id);
        }
      }

      // 2. ITERATE AND UPDATE/CREATE TRIPS PROPERLY WITHOUT DUPLICATES
      for (const tripItem of trips) {
        console.log("Processing trip:", tripItem);
        const tripPartner = partners.find((p) => p.id === tripItem.partnerId || p._id === tripItem.partnerId);
        const tripVehicle = vehicles.find((v) => v.id === tripItem.vehicleId || v._id === tripItem.vehicleId);
        const tripLegs = legs.filter((l) => tripItem.legIds.includes(l.id));

        console.log("Trip legs for trip:", tripLegs);

        // Pass trip_id if editing an existing trip to ensure backend updates it
        const tripPayload = {
          trip_id: tripItem.trip_id || tripItem._id || (tripItem.id && !tripItem.id.startsWith('trip-') ? tripItem.id : undefined),
          name: tripItem.label,
          type: tripType,
          status: tripItem.partnerId ? "assigned" : "draft",
          priority: priority,
          partner_id: tripItem.partnerId || selectedPartnerId || undefined,
          vehicle_id: tripItem.vehicleId || selectedVehicleId || undefined,
          total_weight: sessionShipments.reduce((sum, s) => sum + s.weight, 0),
          total_value: sessionShipments.reduce((sum, s) => sum + s.value, 0),
          estimated_delivery_time: estimatedDelivery,
          notes: notes.trim() || undefined,
          
          route_legs: tripLegs.map((leg, index) => {
            const isDropoff = leg.type.includes("delivery");
            
            return {
              sequence: leg.sequence,
              type: leg.type,
              from: leg.from || sessionShipments[0]?.origin,
              to: leg.to || sessionShipments[0]?.destination,
              status: "pending",
              carrier: leg.carrier || tripPartner?.name,
              vehicle: tripVehicle?.registration,
              driver: tripVehicle?.driver,
              shipments: createdShipmentIds,
              stops: [
                {
                  order_number: sessionShipments[0]?.orders?.[0]?.label || undefined,
                  stopSequence: (index * 2) + 1,
                  stopType: "PICKUP",
                  location: { 
                    address: leg.from || sessionShipments[0]?.origin,
                    latitude: leg.fromLat || null,
                    longitude: leg.fromLng || null
                  },
                  status: "PENDING",
                  po_id: leg.po_id || sessionShipments[0]?.poId || undefined,
                  dg_id: leg.dg_id || sessionShipments[0]?.assignedItemIds || undefined,
                  contact: {
                    name: sessionShipments[0]?.orders?.[0]?.customer,
                    phone: sessionShipments[0]?.orders?.[0]?.customer_phone || null,
                    type: sessionShipments[0]?.orders?.[0]?.customer_type || null
                  }
                },
                {
                  order_number: sessionShipments[0]?.orders?.[0]?.label || undefined,
                  stopSequence: (index * 2) + 2,
                  stopType: isDropoff ? "DROPOFF" : "PICKUP",
                  location: { 
                    address: leg.to || sessionShipments[0]?.destination,
                    latitude: leg.toLat || null,
                    longitude: leg.toLng || null
                   },
                  status: "PENDING",
                  po_id: leg.po_id || sessionShipments[0]?.poId || undefined,
                  dg_id: leg.dg_id || sessionShipments[0]?.assignedItemIds || undefined,
                  contact: isDropoff ? {
                    name: sessionShipments[0]?.orders?.[0]?.customer,
                    phone: sessionShipments[0]?.orders?.[0]?.customer_phone || null,
                    type: sessionShipments[0]?.orders?.[0]?.customer_type || null
                  } : {
                    name: sessionShipments[0]?.orders?.[0]?.customer, 
                    phone: sessionShipments[0]?.orders?.[0]?.customer_phone || null,
                    type: sessionShipments[0]?.orders?.[0]?.customer_type || null
                  }
                }
              ]
            };
          })
        };

        console.log("Submitting trip payload:", tripPayload);

        const tripRes = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-trip`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-token": token },
          body: JSON.stringify(tripPayload)
        });
        const tripResult = await tripRes.json();

        if (tripResult.success && tripResult.data) {
          onCreated(tripResult.data);
        } else {
          throw new Error("Failed to save one or more trips");
        }
      }

      toast.success("Trip(s) successfully saved and updated");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while saving the plan.");
    } finally {
      setLoading(false);
    }
  }; */

const handleCreateTrip = async () => {
  try {
    setLoading(true);
    const token = decryptData(sessionStorage.getItem("token"));
    const createdShipmentIds = [];

    // 1. Create Shipments (keep existing shipment logic)
    for (const shipment of sessionShipments) {
      const shipmentPayload = {
        dg_ids: shipment.assignedItemIds,
        origin: { address: shipment.origin },
        destination: { address: shipment.destination },
        weight: shipment.weight,
        status: "Pending"
      };
      const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-shipment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify(shipmentPayload)
      });
      const result = await res.json();
      if (result.success && result.data) createdShipmentIds.push(result.data._id);
    }

    // Find matching item metadata for PO/DG mapping
    const activeCartItem = step2CartItems.find(item => sessionShipments[0]?.assignedItemIds?.includes(item.id));
    const targetPoId = sessionShipments[0]?.po_id || activeCartItem?.po_id || null;
    const targetDgId = sessionShipments[0]?.dg_id || activeCartItem?.id || null;

    // 2. Iterate and Create/Update Trips with Computed Data
    for (const tripItem of trips) {
      const tripPartner = partners.find((p) => p.id === tripItem.partnerId || p._id === tripItem.partnerId);
      const tripVehicle = vehicles.find((v) => v.id === tripItem.vehicleId || v._id === tripItem.vehicleId);
      const tripLegs = legs.filter((l) => tripItem.legIds.includes(l.id));

      // Calculate total trip metrics from the legs built in Step 4
      const totalDistance = tripLegs.reduce((sum, leg) => sum + parseFloat(leg.distance || 0), 0);
      const totalEstimatedMinutes = tripLegs.reduce((sum, leg) => {
         if (!leg.departurePlanned || !leg.arrivalPlanned) return sum;
         const diff = (new Date(leg.arrivalPlanned) - new Date(leg.departurePlanned)) / 60000;
         return sum + (diff > 0 ? diff : 0);
      }, 0);

      const tripPayload = {
        trip_id: tripItem.trip_id || tripItem._id || (tripItem.id && !tripItem.id.startsWith('trip-') ? tripItem.id : undefined),
        name: tripItem.label,
        type: tripType,
        status: tripItem.partnerId ? "assigned" : "draft",
        priority: priority,
        partner_id: tripItem.partnerId || selectedPartnerId || undefined,
        vehicle_id: tripItem.vehicleId || selectedVehicleId || undefined,
        total_weight: sessionShipments.reduce((sum, s) => sum + s.weight, 0),
        total_value: sessionShipments.reduce((sum, s) => sum + s.value, 0),
        estimated_delivery_time: estimatedDelivery || tripLegs[tripLegs.length - 1]?.arrivalPlanned,
        totalDistance: parseFloat(totalDistance.toFixed(1)),
        totalEstimatedMinutes: Math.round(totalEstimatedMinutes),
        notes: notes.trim() || undefined,
        order_ids: sessionShipments.flatMap(s => s.orders.map(o => o.id)),
        
        route_legs: tripLegs.map((leg, index) => {
          const fromLoc = availableLocations.find(l => l.name === leg.from || l.address === leg.from);
          const toLoc = availableLocations.find(l => l.name === leg.to || l.address === leg.to);

          return {
            sequence: leg.sequence,
            type: leg.type,
            from: leg.from || sessionShipments[0]?.origin,
            to: leg.to || sessionShipments[0]?.destination,
            distance: leg.distance,
            status: "pending",
            carrier: leg.carrier || tripPartner?.name,
            vehicle: tripVehicle?.registration,
            driver: tripVehicle?.driver,
            shipments: createdShipmentIds,
            departurePlanned: leg.departurePlanned ? new Date(leg.departurePlanned).toISOString() : undefined,
            arrivalPlanned: leg.arrivalPlanned ? new Date(leg.arrivalPlanned).toISOString() : undefined,
            stops: [
              {
                stopSequence: (index * 2) + 1,
                stopType: "PICKUP",
                location: { 
                  address: leg.from || sessionShipments[0]?.origin,
                  latitude: fromLoc?.lat || leg.fromLat || null,
                  longitude: fromLoc?.lng || leg.fromLng || null
                },
                status: "PENDING",
                plannedTime: leg.departurePlanned ? new Date(leg.departurePlanned).toISOString() : undefined,
                po_id: targetPoId,
                dg_id: targetDgId
              },
              {
                stopSequence: (index * 2) + 2,
                stopType: "DROPOFF",
                location: { 
                  address: leg.to || sessionShipments[0]?.destination,
                  latitude: toLoc?.lat || leg.toLat || null,
                  longitude: toLoc?.lng || leg.toLng || null
                },
                status: "PENDING",
                plannedTime: leg.arrivalPlanned ? new Date(leg.arrivalPlanned).toISOString() : undefined,
                po_id: targetPoId,
                dg_id: targetDgId
              }
            ]
          };
        })
      };

      const tripRes = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-trip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify(tripPayload)
      });
      const tripResult = await tripRes.json();
      if (tripResult.success && tripResult.data) onCreated(tripResult.data);
    }

    toast.success("Trip(s) successfully saved and updated");
    onClose();
  } catch (error) {
    toast.error("An error occurred while saving the plan.");
  } finally {
    setLoading(false);
  }
};

const notifyRiders = async (tripId) => {
    try {
      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);
  
      const payload = {
        trip_id: tripId, // Uses tripId as requested
        riders: selectedRiders
      };
  
      const res = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/create-shipment-rider-assignment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token || ""
          },
          body: JSON.stringify(payload)
        }
      );
  
      if (!res.ok) {
        throw new Error("Failed to notify riders");
      }
  
      setPopupMessage("Riders notified successfully");
      setPopupType("200or201");
      setShowPopupMessages(true);
      setTimeout(() => {
        setShowPopupMessages(false);
      }, 3000);
  
    } catch (err) {
      console.error(err);
      setPopupMessage("Failed to notify riders. Please try again.");
      setPopupType("500");
      setShowPopupMessages(true);
    }
  };

  const fetchRouteComputation = async (originLoc, destLoc, waypoints = []) => {
  try {
    const token = decryptData(sessionStorage.getItem("token"));
    const payload = {
      origin: { latitude: originLoc.lat, longitude: originLoc.lng },
      destination: { latitude: destLoc.lat, longitude: destLoc.lng },
      waypoints: waypoints.map(wp => ({ latitude: wp.lat, longitude: wp.lng }))
    };

    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/compute-route-and-trip`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-token": token },
      body: JSON.stringify(payload)
    });
    const result = await res.json();
    return result.success ? result.data : null;
  } catch (err) {
    console.error("Failed to compute route via API", err);
    return null;
  }
};

  const stepLabels = ["Select Orders", "Create Shipments", "Delivery & Priority", "Route Builder", "Assign", "Review"];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-2 sm:p-4">
      <div className="w-full max-w-5xl h-[calc(100vh-1rem)] sm:h-[calc(100vh-2rem)] max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] min-h-0 flex flex-col overflow-hidden rounded-xl sm:rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
        <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
          <div>
            <h3 className="font-semibold text-base">Create New Trip(s)</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Create shipments and route legs from selected orders/delivery groups and assign a delivery partner and create trips
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center px-5 py-3 border-b bg-muted/20 flex-shrink-0 gap-0">
          {stepLabels.map((label, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
              <div key={n} className="flex items-center flex-1 last:flex-none">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${done ? "bg-green-500 text-white" : active ? "bg-foreground text-background" : "bg-muted text-muted-foreground border border-border"}`}>
                    {done ? <CheckCircle className="w-4 h-4" /> : n}
                  </div>
                  <span className={`text-sm ${active ? "font-medium" : "text-muted-foreground"}`}>{label}</span>
                </div>
                {i < stepLabels.length - 1 && (<div className={`flex-1 h-px mx-3 ${done ? "bg-green-400" : "bg-border"}`} />)}
              </div>
            );
          })}
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-5">
            {step === 1 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                            <div>
                                <h4 className="font-medium">Select Orders to Ship</h4>
                                <p className="text-sm text-muted-foreground mt-0.5">
                                Pick entire orders or expand to select specific delivery groups
                                </p>
                            </div>
                            </div>

                            <div className="flex items-center">
                            <div className="flex items-center gap-3">
                              <div className="relative flex-1 max-w-xs">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input className="pl-9" placeholder="Search orders..." value={orderSearch} onChange={handleOrderSearchChange} />
                              </div>

                              {/* ── Status / Region Filter Dropdown ── */}
                              <select
                                className="border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                              >
                                <option value="all">All Status</option>
                                {status.map((stat) => (
                                  <option key={stat} value={stat}>
                                    {stat}
                                  </option>
                                ))}
                              </select>
                            </div>
                            </div>

                            {selectedGroups.length > 0 && (
                            <Card className="p-3 bg-blue-50 border-blue-200">
                                <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4 text-sm">
                                    <span className="font-medium text-blue-700">{selectedGroups.length} order{selectedGroups.length !== 1 ? "s" : ""} selected</span>
                                    {/*<span className="text-blue-600">~{Math.round(totalSelectedWeight).toLocaleString()} kg</span>
                                    <span className="text-blue-600">₹{totalSelectedValue.toLocaleString()}</span>*/}
                                </div>
                                <button className="text-xs text-blue-600 hover:underline" onClick={() => setSelectedGroups([])}>
                                    Clear all
                                </button>
                                </div>
                            </Card>
                            )}

                            <div className="space-y-2">
                            {/* ✅ UPDATED: Map over paginatedOrders instead of filteredAvailable */}
                            {paginatedOrders.map((order) => {
                              const grp = selectedGroups.find((g) => g.orderId === order._id);
                              const isFullSelected = grp && grp.itemIds.length === 0;
                              const isPartialSelected = grp && grp.itemIds.length > 0;
                              const isExpanded = expandedOrder === order._id;
                              
                              // Determine if the overall order is ready or done
                              const orderStatus = order.delivery_groups?.[0]?.delivery_group_status?.trim()?.toLowerCase() || '';
                              const isOrderReady = orderStatus !== 'waiting for delivery schedule';
                              const isDone = orderStatus === 'delivered';
                              const isOrderNotReady = orderStatus === 'not ready';
                              
                              // Disable selection if not ready, done, or explicitly "not ready"
                              const isOrderDisabled = !isOrderReady || isDone || isOrderNotReady;

                              return (
                                <Card 
                                  key={order._id} 
                                  className={`overflow-hidden transition-colors ${
                                    isFullSelected ? "border-blue-400 bg-blue-50/40" : isPartialSelected ? "border-blue-200 bg-blue-50/20" : ""
                                  }`}
                                >
                                  {/* 1. Order Header & Order Data */}
                                  <div className="p-4 bg-background">
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-start gap-3">
                                      <button 
                                        onClick={() => { if (!isOrderDisabled) toggleOrder(order._id); }} 
                                        disabled={isOrderDisabled}
                                        className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                          isOrderDisabled
                                            ? "border-gray-200 bg-gray-100 cursor-not-allowed opacity-50" 
                                            : isFullSelected 
                                              ? "bg-blue-500 border-blue-500" 
                                              : isPartialSelected 
                                                ? "bg-blue-200 border-blue-400" 
                                                : "border-border hover:border-blue-400"
                                        }`}
                                      >
                                        {isFullSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                        {isPartialSelected && <span className="w-2 h-0.5 bg-blue-600 rounded" />}
                                      </button>
                                        
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                            <span className="text-base font-semibold">{order.order_number}</span>

                                            {/* Order Data Below Order Number */}
                                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
{/*                                             <span className="font-medium text-gray-900 flex items-center gap-1">
                                              <Users className="w-3.5 h-3.5" /> {order.customer_name}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <MapPin className="w-3.5 h-3.5" /> {order.city}, {order.state}
                                            </span>
                                            <span className="font-semibold text-gray-900">
                                              ₹{order?.total?.toLocaleString()}
                                            </span> */}
                                            <span className="flex items-center gap-1">
                                              <Clock className="w-3.5 h-3.5" /> {order.salesorder_date ? formatDate(order.salesorder_date) : order.createdAt}
                                            </span>
                                            {/* NEW: Display Distance and ETA */}
                                            <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-medium">
                                              📍 {order.distance} km
                                            </span>
                                            <span className="flex items-center gap-1 text-purple-600 bg-purple-50 px-2 py-0.5 rounded font-medium">
                                               ETA: {formatEstimatedTime(order.delivery_eta)}
                                            </span>
                                          </div>
                                            
                                            {(order.priority) && (
                                              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                                                order.priority === "high" ? "bg-red-100 text-red-700" : order.priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"
                                              }`}>
                                                {order.priority}
                                              </span>
                                            )}

                                            {/* Ready / Not Ready / Done Status */}
                                            {order.delivery_groups?.[0]?.delivery_group_status && (
                                              isDone ? (
                                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold uppercase tracking-wider">
                                                  <CheckCircle className="w-3 h-3" />
                                                  Done
                                                </span>
                                              ) : isOrderNotReady || !isOrderReady ? (
                                                <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold uppercase tracking-wider">
                                                  <Clock className="w-3 h-3" />
                                                  Not ready
                                                </span>
                                              ) : (
                                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold uppercase tracking-wider">
                                                  <CheckCircle className="w-3 h-3" />
                                                  Ready
                                                </span>
                                              )
                                            )}
                                          </div>
                                          
                                          {/* Order Data Below Order Number */}
                                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                                            <span className="font-medium text-gray-900 flex items-center gap-1">
                                              <Users className="w-3.5 h-3.5" /> {order.customer_name}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <MapPin className="w-3.5 h-3.5" /> {order.city}, {order.state}
                                            </span>
                                            <span className="font-semibold text-gray-900">
                                              ₹{order?.total?.toLocaleString()}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <Clock className="w-3.5 h-3.5" /> {order.salesorder_date ? formatDate(order.salesorder_date) : order.createdAt}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                                        {isPartialSelected && (
                                          <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded">
                                            {grp.itemIds.length}/{order.delivery_groups?.length || 0} selected
                                          </span>
                                        )}
                                        <button 
                                          className="p-1.5 hover:bg-muted rounded-md transition-colors text-muted-foreground border" 
                                          onClick={() => setExpandedOrder(isExpanded ? null : order._id)}
                                        >
                                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* 2. Delivery Groups Section */}
                                  {isExpanded && (
                                    <div className="border-t bg-slate-50/50 p-4 space-y-3">
                                      <div className="flex items-center gap-2 mb-3">
                                        <Package className="w-4 h-4 text-muted-foreground" />
                                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                                          Delivery Groups ({order.delivery_groups?.length || 0})
                                        </p>
                                      </div>

                                      <div className="space-y-3">
                                        {order?.delivery_groups?.map((item) => {
                                          const itemSelected = isItemSelected(order._id, item._id);
                                          const isGroupExpanded = expandedDeliveryGroups[item._id];
                                          
                                          // Check if specific delivery group is ready, done, or not ready
                                          const dgStatus = item.delivery_group_status?.trim()?.toLowerCase() || '';
                                          const isItemReady = dgStatus !== 'waiting for delivery schedule';
                                          const isItemDone = dgStatus === 'delivered';
                                          const isItemNotReady = dgStatus === 'not ready';
                                          const isItemDisabled = !isItemReady || isItemDone || isItemNotReady;

                                          return (
                                            <div 
                                              key={item._id} 
                                              className={`w-full flex flex-col rounded-lg transition-all border shadow-sm ${
                                                itemSelected ? "bg-blue-50/50 border-blue-300 ring-1 ring-blue-200" : "bg-white border-gray-200 hover:border-gray-300"
                                              }`}
                                            >
                                              {/* DG Header */}
                                              <div 
                                                className={`flex items-center gap-3 px-4 py-3 w-full text-left ${!isItemDisabled ? "cursor-pointer" : "cursor-not-allowed opacity-75"}`}
                                                onClick={() => { if (!isItemDisabled) toggleItem(order._id, item._id, order.delivery_groups.map((i) => i._id)); }}
                                              >
                                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                                  isItemDisabled 
                                                    ? "border-gray-200 bg-gray-100" 
                                                    : itemSelected 
                                                      ? "bg-blue-500 border-blue-500" 
                                                      : "border-gray-300"
                                                }`}>
                                                  {itemSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                                </div>
                                                
                                                {/* DG Header */}
                                                <div className="flex-1 grid grid-cols-5 gap-4 items-center">
                                                  <span className="text-sm font-semibold text-gray-900">{item.delivery_group_number}</span>
                                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <Route className="w-3 h-3" /> {item.delivered_to || "Hub"}
                                                  </span>
                                                  <span className="text-xs font-medium text-blue-600">
                                                    {item.distance} km
                                                  </span>
                                                  <span className="text-xs font-medium text-purple-600">
                                                    ETA: {formatEstimatedTime(item.delivery_eta)}
                                                  </span>
                                                  <span className="text-xs flex items-center justify-end gap-2">
                                                    <Badge variant="outline" className="text-[10px] bg-gray-50">{item.delivery_group_status}</Badge>
                                                  </span>
                                                </div>

                                                <button 
                                                  onClick={(e) => toggleGroupExpand(e, item._id)} 
                                                  className="p-1 hover:bg-gray-100 rounded text-muted-foreground transition-colors ml-2 border"
                                                >
                                                  {isGroupExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>
                                              </div>

                                              {/* 3. Items inside DG */}
                                              {isGroupExpanded && (
                                                <div className="border-t border-dashed border-gray-200 bg-gray-50/50 px-4 py-3 rounded-b-lg">
                                                  {item.itemsData && item.itemsData.length > 0 ? (
                                                    <div className="space-y-2">
                                                      {item.itemsData.map((itm, idx) => (
                                                        <div key={idx} className="flex items-center gap-4 bg-white p-2.5 rounded-md border border-gray-100 shadow-sm">
                                                          {console.log("Rendering item:", itm)}
                                                          {itm.image ? (
                                                          <div className="w-10 h-10 rounded border bg-white flex-shrink-0 overflow-hidden">
                                                            <img 
                                                              src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${itm.image}`} 
                                                              alt={itm.name} 
                                                              className="w-full h-full object-cover" // Changed h-0 to h-full
                                                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                            />
                                                            <div className="w-full h-full hidden items-center justify-center bg-gray-100 text-gray-400">
                                                              <Package className="w-5 h-5" />
                                                            </div>
                                                          </div>
                                                          ) : (
                                                            <div className="w-10 h-10 rounded border bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-400">
                                                              <Package className="w-5 h-5" />
                                                            </div>
                                                          )}
                                                          
                                                          <div className="flex flex-col flex-1 min-w-0">
                                                            <span className="text-sm font-medium text-gray-900 truncate">{itm.name}</span>
                                                            <span className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                                                              <Warehouse className="w-3 h-3" /> {itm.awardedTo || "--"}
                                                            </span>
                                                          </div>
                                                          <div className="text-right flex-shrink-0">
                                                            <span className="text-sm font-semibold text-gray-900">₹{itm.finalPrice?.toLocaleString()}</span>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <p className="text-xs text-muted-foreground italic py-2 text-center">No items found in this group.</p>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </Card>
                              );
                            })}


                            
                            {paginatedOrders.length === 0 && (
                                <div className="text-center py-6 text-muted-foreground text-sm border rounded-lg">
                                No orders found.
                                </div>
                            )}
                            </div>

                            {/* ✅ ADDED: Pagination controls */}
                            {step1TotalPages > 1 && (
                            <div className="mt-4 pt-2 border-t">
                                <Pagination
                                currentPage={step1CurrentPage}
                                totalPages={step1TotalPages}
                                totalItems={step1TotalItems}
                                itemsPerPage={5}
                                onPageChange={handleStep1PageChange}
                                onPreviousPage={handleStep1PreviousPage}
                                onNextPage={handleStep1NextPage}
                                showInfo={true}
                                itemName="Orders"
                                />
                            </div>
                            )}
                        </div>
                        )}

            {step === 2 && (
                          <div className="space-y-5">  
                            <div>
                              <h4 className="font-medium">Create Shipments</h4>
                              <p className="text-sm text-muted-foreground mt-0.5">Select items (optional), origin, and destination to create shipments</p>
                            </div>

                            <Card className="p-5 border-2 border-muted bg-card shadow-sm space-y-5">
                            {/* 1. SELECTED ORDERS (Top of the form) */}
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                  Select Items for this Shipment <span className="text-blue-500 lowercase">(Optional)</span>
                                </p>
                              </div>
                              
                              {step2CartItems.length === 0 ? (
                                <div className="text-center py-4 bg-muted/20 rounded-lg border border-dashed">
                                  <Package className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                                  <p className="text-xs text-muted-foreground">No items selected from Step 1. You can create a standalone shipment below.</p>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                  {step2CartItems.map((item) => {
                                    const isSelected = step2SelectedItems.includes(item.id);
                                    const isAlreadyShipped = sessionShipments.some(s => s.assignedItemIds?.includes(item.id));
                                    
                                    return (
                                      <div 
                                        key={item.id} 
                                        onClick={() => { if(!isAlreadyShipped) toggleStep2Item(item.id); }}
                                        className={`p-2.5 border rounded-lg text-sm flex justify-between items-center transition-all ${
                                          isAlreadyShipped 
                                            ? "bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed" 
                                            : isSelected 
                                              ? "bg-blue-50 border-blue-400 ring-1 ring-blue-200 cursor-pointer" 
                                              : "bg-background hover:border-blue-300 cursor-pointer"
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <div className={`w-4 h-4 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                                            isAlreadyShipped ? "bg-gray-300 border-gray-300" : isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"
                                          }`}>
                                            {(isSelected || isAlreadyShipped) && <CheckCircle className="w-3 h-3 text-white" />}
                                          </div>
                                          <div>
                                            <span className="font-mono font-bold text-xs block">{item.label}</span>
                                            <span className="text-[10px] text-muted-foreground truncate">{item.customer}</span>
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end flex-shrink-0 ml-2">
                                          <span className="text-[9px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded uppercase">
                                            {item.type}
                                          </span>
                                          {isAlreadyShipped && <span className="text-[9px] font-bold text-green-600 mt-1">ASSIGNED</span>}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* 2. ORIGIN & DESTINATION */}
                            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 border-t pt-5">
                              {/* ORIGIN */}
                              <div className="space-y-4">
                                <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Origin</h5>
                                <div>
                                  <label className="text-xs font-medium text-blue-600 mb-1 block">Quick Select Location</label>
                                  <select 
                                    className="w-full border-2 border-blue-200 rounded-lg p-2 text-sm bg-blue-50/30 text-blue-900 focus:outline-none focus:border-blue-400"
                                    onChange={(e) => {
                                      const loc = availableLocations.find(l => l.id === e.target.value);
                                      if (loc) {
                                        setOriginCompany(loc.name);
                                        setOriginAddress(loc.address);
                                        setOriginCity(loc.city);
                                        setOriginState(loc.state);
                                        setOrigin(loc.name);
                                      }
                                    }}
                                  >
                                    <option value="">-- Choose Supplier / Hub --</option>
                                    {availableLocations./* filter(l => l.type !== "customer"). */map(loc => (
                                      <option key={loc.id} value={loc.id}>{loc.label} - {loc.address.substring(0, 25)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block mt-2">Company / Facility Name</label>
                                  <Input value={originCompany} onChange={(e) => setOriginCompany(e.target.value)} placeholder="e.g. Terminal HQ-01" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">City</label>
                                    <Input value={originCity} onChange={(e) => setOriginCity(e.target.value)} placeholder="City" />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">State</label>
                                    <Input value={originState} onChange={(e) => setOriginState(e.target.value)} placeholder="State" />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Pickup Date & Time</label>
                                  <Input type="datetime-local" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} />
                                </div>
                              </div>

                              {/* DIVIDER */}
                              <div className="hidden md:flex flex-col items-center justify-center relative">
                                <div className="absolute top-0 bottom-0 w-px border-l-2 border-dashed border-gray-200"></div>
                                <div className="relative z-10 w-8 h-8 rounded-full border border-gray-200 bg-background flex items-center justify-center text-muted-foreground">
                                  <ArrowRight className="w-4 h-4" />
                                </div>
                              </div>

                              {/* DESTINATION */}
                              <div className="space-y-4">
                                <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Destination</h5>
                                <div>
                                  <label className="text-xs font-medium text-green-600 mb-1 block">Quick Select Location</label>
                                  <select 
                                    className="w-full border-2 border-green-200 rounded-lg p-2 text-sm bg-green-50/30 text-green-900 focus:outline-none focus:border-green-400"
                                    onChange={(e) => {
                                      const loc = availableLocations.find(l => l.id === e.target.value);
                                      if (loc) {
                                        setDestCompany(loc.name);
                                        setDestAddress(loc.address);
                                        setDestCity(loc.city);
                                        setDestState(loc.state);
                                      }
                                    }}
                                  >
                                    <option value="">-- Choose Customer / Hub --</option>
                                    {availableLocations./* filter(l => l.type !== "supplier"). */map(loc => (
                                      <option key={loc.id} value={loc.id}>{loc.label} - {loc.address.substring(0, 25)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block mt-2">Consignee Name</label>
                                  <Input value={destCompany} onChange={(e) => setDestCompany(e.target.value)} placeholder="Enter company name" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">City</label>
                                    <Input value={destCity} onChange={(e) => setDestCity(e.target.value)} placeholder="City" />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">State</label>
                                    <Input value={destState} onChange={(e) => setDestState(e.target.value)} placeholder="State" />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Expected Delivery</label>
                                  <Input type="datetime-local" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
                                </div>
                              </div>
                            </div>

                            {/* Conditional Manual Inputs: Display if NO items are selected */}
                            {(!step2SelectedItems || step2SelectedItems.length === 0) && (
                                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                                    <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Total Weight (kg)</label>
                                    <Input type="number" placeholder="e.g. 500" value={manualWeight} onChange={(e) => setManualWeight(e.target.value)} />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Total Value (₹)</label>
                                    <Input type="number" placeholder="e.g. 15000" value={manualValue} onChange={(e) => setManualValue(e.target.value)} />
                                </div>
                                </div>
                            )}

                            {/* 3. SAVE SHIPMENT BUTTON */}
                            <div className="pt-2 flex justify-end">
                                <Button onClick={handleAddShipment} className="bg-foreground text-background">
                                <Plus className="w-4 h-4 mr-2" />
                                Add Shipment to Trip
                                </Button>
                            </div>
                            </Card>

                            {/* 4. CREATED SHIPMENT CARDS */}
                            {sessionShipments.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Shipments in this Trip ({sessionShipments.length})</h4>
                                {sessionShipments.map(s => (
                                <Card key={s.id} className="p-4 border-l-4 border-l-blue-500 bg-blue-50/20">
                                    <div className="flex justify-between items-center">
                                    <div>
                                        <p className="font-bold text-sm flex items-center gap-2">
                                        {s.id} <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none">{s.orders.length} Orders attached</Badge>
                                        </p>
                                        <p className="text-xs font-medium mt-1">{s.origin} <ArrowRight className="inline w-3 h-3 text-muted-foreground mx-1"/> {s.destination}</p>
                                    </div>
                                    <div className="text-right text-xs">
                                        <p className="text-muted-foreground">{s.weight} kg</p>
                                        <p className="text-muted-foreground font-medium mt-0.5">₹{s.value.toLocaleString()}</p>
                                    </div>
                                    </div>
                                </Card>
                                ))}
                            </div>
                            )}
                          </div>
                        )}

              {step === 3 && (
                <div className="space-y-5">
                  <div>
                    <h4 className="font-medium">Delivery Type & Priority</h4>
                    <p className="text-sm text-muted-foreground mt-0.5">Configure how these shipments will be prioritized and delivered</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                <div>
                                  <label className="text-sm font-medium">Delivery Type</label>
                                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                                    {["direct", "hub_and_spoke"].map((t) => (
                                      <button key={t} onClick={() => {
                                        setTripType(t);
                                        if (t === "hub_and_spoke" && legs.length === 1) {
                                          setLegs([
                                            { id: "l1", sequence: 1, type: "supplier_to_hub", from: origin || "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "" },
                                            { id: "l2", sequence: 2, type: "hub_to_delivery", from: "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "" },
                                          ]);
                                        } else if (t === "direct") {
                                          setLegs([{ id: "l1", sequence: 1, type: "direct", from: origin || "", to: "", departurePlanned: "", arrivalPlanned: "", carrier: "" }]);
                                        }
                                      }} className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${tripType === t ? "border-blue-500 bg-blue-50 text-blue-700" : "border-border hover:border-foreground"}`}>
                                        <div className="flex items-center gap-2">
                                          {t === "direct" ? <Navigation className="w-4 h-4" /> : <Warehouse className="w-4 h-4" />}
                                          {t === "direct" ? "Direct" : "Hub & Spoke"}
                                        </div>
                                        <p className="text-xs mt-1 opacity-70">
                                          {t === "direct" ? "Supplier → Customer" : "Via hub/warehouse"}
                                        </p>
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                <div>
                                  <label className="text-sm font-medium">Priority</label>
                                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                                    {["high", "medium", "low"].map((p) => (
                                      <button key={p} onClick={() => setPriority(p)} className={`p-2 rounded-lg border text-xs font-medium transition-colors ${priority === p ? p === "high" ? "border-red-500 bg-red-50 text-red-700" : p === "medium" ? "border-yellow-400 bg-yellow-50 text-yellow-700" : "border-gray-400 bg-gray-50 text-gray-700" : "border-border hover:border-foreground text-muted-foreground"}`}>
                                        {p.charAt(0).toUpperCase() + p.slice(1)}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                <div className="col-span-2">
                                  <label className="text-sm font-medium">Notes / Special Instructions</label>
                                  <textarea className="w-full mt-1.5 border rounded-lg p-3 text-sm resize-none bg-background focus:outline-none focus:ring-2 focus:ring-ring" rows={2} placeholder="Handling instructions, temperature requirements, delivery windows..." value={notes} onChange={(e) => setNotes(e.target.value)} />
                                </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Route Builder</h4>
                      <p className="text-sm text-muted-foreground mt-0.5">Design the full journey — legs, hubs, return trips, linked dispatches</p>
                    </div>
                  </div>
                                 <div>
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide font-medium">
                    Start from a template
                </p>
                <div className="grid grid-cols-4 gap-2">
                    {[
                    { key: "direct", label: "Direct", sub: "A → B", icon: "→" },
                    { key: "hub_spoke", label: "Hub & Spoke", sub: "Sup → Hub → Cust", icon: "⬡" },
                    { key: "round_trip_full", label: "Round Trip", sub: "Hub → Sup → Hub → Cust → Hub", icon: "↺" },
                    { key: "multi_trip_linked", label: "Multi-Trip", sub: "Trip 1 → handoff → Trip 2", icon: "⇉" },
                    ].map((tpl) => {
                    const isSelected = selectedTemplate === tpl.key;

                    return (
                        <button
                        key={tpl.key}
                        onClick={() => applyTemplate(tpl.key)}
                        className={`p-3 border-2 rounded-xl text-left transition-all ${
                            isSelected
                            ? "border-blue-500 bg-blue-50/80 text-blue-900 shadow-sm"
                            : "border-border bg-card hover:border-blue-300 hover:bg-accent/30"
                        }`}
                        >
                        <div className={`text-xl mb-1 ${isSelected ? "text-blue-600" : ""}`}>
                            {tpl.icon}
                        </div>
                        <p className={`text-xs font-semibold ${isSelected ? "text-blue-700" : ""}`}>
                            {tpl.label}
                        </p>
                        <p className={`text-xs mt-0.5 leading-tight ${isSelected ? "text-blue-600/80" : "text-muted-foreground"}`}>
                            {tpl.sub}
                        </p>
                        </button>
                    );
                    })}
                </div>
                </div>

                  <div className="flex items-center gap-2">
                    <p className="text-xs text-muted-foreground font-medium">Mode:</p>
                    {["linear", "round_trip", "multi_trip"].map((m) => (
                      <button key={m} onClick={() => setRouteMode(m)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${routeMode === m ? "border-blue-500 bg-blue-50 text-blue-700" : "border-border hover:border-foreground"}`}>
                        {m === "linear" ? "Linear" : m === "round_trip" ? "↺ Round Trip" : "⇉ Multi-Trip"}
                      </button>
                    ))}
                  </div>

                  <RouteCanvas legs={legs} trips={trips} routeMode={routeMode} />

                  <div className="space-y-5">
                    {trips.map((trip, ti) => {
                      const tripLegs = legs.filter((l) => trip.legIds.includes(l.id));
                      return (
                        <div key={trip.id}>
                          <div className={`flex items-center justify-between px-4 py-2.5 rounded-t-xl border-2 ${trip.borderColor} bg-white`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${trip.color}`} />
                              <Input className="border-0 p-0 h-auto text-sm font-semibold bg-transparent focus-visible:ring-0 w-48" value={trip.label} onChange={(e) => updateTrip(trip.id, "label", e.target.value)} />
                              {trip.linkedFromTripId && (
                                <div className="flex items-center gap-1 bg-yellow-50 border border-yellow-200 rounded-full px-2 py-0.5">
                                  <ArrowRight className="w-3 h-3 text-yellow-600" />
                                  <span className="text-xs text-yellow-700 font-medium">
                                    Linked — starts after {trips.find((t) => t.id === trip.linkedFromTripId)?.label}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {trip.linkedFromTripId && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-muted-foreground">Handoff at:</span>
                                  <Input className="h-7 text-xs w-40" placeholder="e.g. Delhi Hub" value={trip.handoffPoint || ""} onChange={(e) => updateTrip(trip.id, "handoffPoint", e.target.value)} />
                                </div>
                              )}
                              {trips.length > 1 && ti === trips.length - 1 && (
                                <button onClick={() => removeTrip(trip.id)} className="text-muted-foreground hover:text-red-500 p-1">
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className={`border-2 border-t-0 ${trip.borderColor} rounded-b-xl p-3 space-y-2`}>
                            {tripLegs.map((leg, legIdx) => (
                              <Card key={leg.id} className={`p-3 ${leg.isReturn ? "bg-gray-50 border-dashed" : ""}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${trip.color}`}>
                                      {leg.sequence}
                                    </span>
                                    <select className="text-xs border rounded px-2 py-1 bg-background" value={leg.type} onChange={(e) => updateLeg(leg.id, "type", e.target.value)}>
                                      {Object.entries(LEG_TYPE_LABELS).map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
                                    </select>
                                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                                      <input type="checkbox" checked={!!leg.isReturn} onChange={(e) => updateLeg(leg.id, "isReturn", e.target.checked)} className="w-3 h-3" />
                                      Return leg
                                    </label>
                                  </div>
                                  {tripLegs.length > 1 && (
                                    <button onClick={() => removeLeg(leg.id)} className="text-muted-foreground hover:text-red-500 p-0.5">
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-xs text-muted-foreground">From</label>
                                    <select 
                                      className="mt-1 w-full text-sm h-8 border rounded px-2 bg-background focus:outline-none focus:ring-1" 
                                      value={leg.from} 
                                      onChange={(e) => updateLeg(leg.id, "from", e.target.value)}
                                    >
                                      <option value="">Select Location...</option>
                                      {availableLocations.map(l => (
                                        <option key={l.id} value={l.name}>{l.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground">To</label>
                                    <select 
                                      className="mt-1 w-full text-sm h-8 border rounded px-2 bg-background focus:outline-none focus:ring-1" 
                                      value={leg.to} 
                                      onChange={(e) => updateLeg(leg.id, "to", e.target.value)}
                                    >
                                      <option value="">Select Location...</option>
                                      {availableLocations.map(l => (
                                        <option key={l.id} value={l.name}>{l.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground">Depart (planned)</label>
                                    <Input className="mt-1 text-sm h-8" type="datetime-local" value={leg.departurePlanned} onChange={(e) => updateLeg(leg.id, "departurePlanned", e.target.value)} />
                                  </div>
{/*                                   <div>
                                    <label className="text-xs text-muted-foreground">Arrive (planned)</label>
                                    <Input className="mt-1 text-sm h-8" type="datetime-local" value={leg.arrivalPlanned} onChange={(e) => updateLeg(leg.id, "arrivalPlanned", e.target.value)} />
                                  </div> */}
                                  {/* Inside Step 4 Leg Mapping -> where Arrive (planned) is rendered */}
                                  <div>
                                    <div className="flex items-center justify-between">
                                      <label className="text-xs text-muted-foreground">Arrive (planned)</label>
                                      {leg.distance && (
                                        <span className="text-[10px] text-blue-600 font-bold bg-blue-50 px-1 rounded">
                                          {leg.distance} km
                                        </span>
                                      )}
                                    </div>
                                    <Input 
                                      className="mt-1 text-sm h-8" 
                                      type="datetime-local" 
                                      value={leg.arrivalPlanned} 
                                      onChange={(e) => updateLeg(leg.id, "arrivalPlanned", e.target.value)} 
                                    />
                                  </div>
                                <div className="col-span-2">
                                  <label className="text-xs text-muted-foreground">Carrier for this leg</label>
                                  <select 
                                    className="mt-1 w-full text-sm h-8 border rounded px-2 bg-background focus:outline-none focus:ring-1" 
                                    value={leg.carrier} 
                                    onChange={(e) => updateLeg(leg.id, "carrier", e.target.value)}
                                  >
                                    <option value="">Select Carrier...</option>
                                    {["Brickbucket", "3 PL", "On Demand", "Supplier"].map((fleet) => (
                                      <option key={fleet} value={fleet}>
                                        {fleet}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                </div>
                              </Card>
                            ))}

                            <div className="flex items-center gap-2 pt-1">
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addLeg(trip.id, "hub_to_delivery")}>
                                <Plus className="w-3 h-3 mr-1" /> Add Leg
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addReturnLeg(trip.id)}>
                                ↩ Add Return Leg
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {routeMode === "multi_trip" && (
                      <button onClick={addLinkedTrip} className="w-full py-3 border-2 border-dashed rounded-xl text-sm text-muted-foreground hover:border-foreground hover:text-foreground transition-colors flex items-center justify-center gap-2">
                        <Plus className="w-4 h-4" />
                        Add Linked Trip — picks up where previous trip drops off
                      </button>
                    )}
                  </div>
                </div>
              )}

{/*               {step === 5 && (() => {
                const currentAssignTripId = activeAssignTripId || trips[0]?.id;
                const activeTrip = trips.find(t => t.id === currentAssignTripId);

                 return (
              <div className="space-y-5">
                <div>
                  <h4 className="font-medium">Assign Delivery Partner & Vehicle</h4>
                  <p className="text-sm text-muted-foreground mt-0.5">Assign a 3PL, courier, or own-fleet vehicle for each individual trip</p>
                </div>

                {trips.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-2 border-b border-border">
                    {trips.map(trip => {
                      const isActive = currentAssignTripId === trip.id;
                      return (
                        <button
                          key={trip.id}
                          onClick={() => setActiveAssignTripId(trip.id)}
                          className={`px-4 py-2.5 rounded-t-lg border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                            isActive 
                              ? 'border-blue-500 text-blue-700 bg-blue-50/50' 
                              : 'border-transparent text-muted-foreground hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${trip.color}`} />
                            {trip.label}
                            {trip.partnerId && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {activeTrip && (
                  <div className="space-y-5 animate-in fade-in duration-300">
                    <div>
                      <p className="text-sm font-medium mb-2">Delivery Partner for <span className="text-blue-700">{activeTrip.label}</span></p>
                      <div className="grid grid-cols-2 gap-3">
                        {partners.filter((p) => p.status === "active").map((p) => {
                          const typeCfg = partnerTypeConfig[p.type];
                          const isSelected = activeTrip.partnerId === p.id;
                          
                          return (
                            <button 
                              key={p.id} 
                              onClick={() => { 
                                updateTrip(activeTrip.id, "partnerId", p.id); 
                                updateTrip(activeTrip.id, "vehicleId", ""); // Reset vehicle if partner changes
                              }} 
                              className={`text-left p-4 rounded-xl border-2 transition-colors ${isSelected ? "border-foreground bg-foreground/5" : "border-border hover:border-foreground/40"}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeCfg.color}`}>{typeCfg.label}</span>
                                  {isSelected && <CheckCircle className="w-4 h-4 text-green-500" />}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                  <span className="text-xs font-medium">{p.rating}</span>
                                </div>
                              </div>
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{p.contactName}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className={`text-xs font-bold ${p.onTimeRate >= 90 ? "text-green-600" : p.onTimeRate >= 80 ? "text-yellow-600" : "text-red-600"}`}>
                                  {p.onTimeRate}% on-time
                                </span>
                                <span className="text-xs text-muted-foreground">{p.activeTrips} active</span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {p.coverageZones.slice(0, 3).map((z) => (<span key={z} className="text-xs bg-muted px-1.5 py-0.5 rounded">{z}</span>))}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {activeTrip.partnerId && (
                      <div>
                        <p className="text-sm font-medium mb-2">
                          Vehicle <span className="text-muted-foreground font-normal">(optional — assign now or later)</span>
                        </p>
                        <div className="space-y-2">
                          {vehicles
                            .filter((v) => v.partnerId === activeTrip.partnerId || !v.partnerId)
                            .map((v) => {
                              const statusCfg = vehicleStatusConfig[v.status];
                              const isAvail = v.status === "available";
                              const isSelected = activeTrip.vehicleId === v.id;
                              
                              return (
                                <button 
                                  key={v.id} 
                                  disabled={!isAvail} 
                                  onClick={() => updateTrip(activeTrip.id, "vehicleId", isSelected ? "" : v.id)} 
                                  className={`w-full text-left flex items-center gap-4 p-3 rounded-lg border-2 transition-colors ${isSelected ? "border-foreground bg-foreground/5" : isAvail ? "border-border hover:border-foreground/40" : "border-border opacity-50 cursor-not-allowed"}`}
                                >
                                  <span className="text-xl flex-shrink-0">{vehicleTypeIcon[v.type]}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm font-medium">{v.registration}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusCfg.color}`}>{statusCfg.label}</span>
                                      {isSelected && <CheckCircle className="w-4 h-4 text-green-500" />}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                                      <span>{v.driver}</span>
                                      <span>·</span>
                                      <span>{v.driverPhone}</span>
                                      <span>·</span>
                                      <span>Cap: {v.capacity}</span>
                                      {v.currentLocation && <><span>·</span><span>📍 {v.currentLocation}</span></>}
                                    </div>
                                  </div>
                                  {v.assignedTrip && (<span className="text-xs text-blue-600 font-medium flex-shrink-0">{v.assignedTrip}</span>)}
                                </button>
                              );
                            })}
                          {vehicles.filter((v) => v.partnerId === activeTrip.partnerId || !v.partnerId).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-6">No vehicles linked to this partner</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
              })()} */}

              {step === 5 && (() => {
                const currentAssignTripId = activeAssignTripId || trips[0]?.id;
                const activeTrip = trips.find(t => t.id === currentAssignTripId);

                // Local state or tracking for selection hierarchy if needed, 
                // or leveraging activeTrip fields (e.g., selectedFleet, selectedVehicleType, partnerId)
                const selectedFleet = activeTrip?.fleet || "";
                const selectedVehicleType = activeTrip?.vehicleType || "";

                return (
                  <div className="space-y-5">
                    <div>
                      <h4 className="font-medium">Assign Fleet, Vehicle & Partner</h4>
                      <p className="text-sm text-muted-foreground mt-0.5">Select Fleet category, Vehicle type, and then choose the corresponding driver/partner</p>
                    </div>

                    {trips.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto pb-2 border-b border-border">
                        {trips.map(trip => {
                          const isActive = currentAssignTripId === trip.id;
                          return (
                            <button
                              key={trip.id}
                              onClick={() => setActiveAssignTripId(trip.id)}
                              className={`px-4 py-2.5 rounded-t-lg border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                                isActive 
                                  ? 'border-blue-500 text-blue-700 bg-blue-50/50' 
                                  : 'border-transparent text-muted-foreground hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-2.5 h-2.5 rounded-full ${trip.color}`} />
                                {trip.label}
                                {trip.partnerId && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {activeTrip && (
                      <div className="space-y-5 animate-in fade-in duration-300">
                        {/* 1. SELECT FLEET */}
                        <div>
                          <label className="text-sm font-medium mb-2 block">1. Select Fleet</label>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {["Brickbucket", "3 PL", "On Demand", "Supplier"].map((fleetOption) => {
                              const isSelected = selectedFleet === fleetOption;
                              return (
                                <button
                                  key={fleetOption}
                                  onClick={() => {
                                    updateTrip(activeTrip.id, "fleet", fleetOption);
                                    updateTrip(activeTrip.id, "vehicleType", "");
                                    updateTrip(activeTrip.id, "partnerId", "");
                                  }}
                                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                                    isSelected ? "border-blue-600 bg-blue-50/50 text-blue-900" : "border-border hover:border-gray-400"
                                  }`}
                                >
                                  <p className="font-semibold text-sm">{fleetOption}</p>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* 2. SELECT VEHICLE TYPE */}
                        {selectedFleet && (
                          <div>
                            <label className="text-sm font-medium mb-2 block">2. Select Vehicle Type</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              {["Bike", "Passenger Auto", "Truck Auto", "Truck Van"].map((vType) => {
                                const isSelected = selectedVehicleType === vType;
                                return (
                                  <button
                                    key={vType}
                                    onClick={() => {
                                      updateTrip(activeTrip.id, "vehicleType", vType);
                                      updateTrip(activeTrip.id, "partnerId", "");
                                    }}
                                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                                      isSelected ? "border-blue-600 bg-blue-50/50 text-blue-900" : "border-border hover:border-gray-400"
                                    }`}
                                  >
                                    <p className="font-semibold text-sm">{vType}</p>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* 3. SELECT PARTNER / DRIVER */}
                        {selectedVehicleType && (
                          <div>
                            <label className="text-sm font-medium mb-2 block">3. Select Partner / Driver</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {partners
                                .filter((p) => !selectedVehicleType || p.vehicle_type?.toLowerCase() === selectedVehicleType.toLowerCase())
                                .map((p) => {
                                  const isSelected = activeTrip.partnerId === p._id;
                                  return (
                                    <button
                                      key={p._id}
                                      onClick={() => updateTrip(activeTrip.id, "partnerId", p._id)}
                                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                                        isSelected ? "border-blue-600 bg-blue-50/50 shadow-sm" : "border-border hover:border-gray-400"
                                      }`}
                                    >
                                      <div className="flex justify-between items-start mb-1">
                                        <p className="font-semibold text-sm">{p.name}</p>
                                        {isSelected && <CheckCircle className="w-4 h-4 text-green-600" />}
                                      </div>
                                      <p className="text-xs text-muted-foreground">📱 {p.mobile} · 🚗 {p.vehicle_number}</p>
                                    </button>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {step === 6 && (
              <div className="space-y-5">
                <div>
                  <h4 className="font-medium">Review & Create Trip(s)</h4>
                  <p className="text-sm text-muted-foreground mt-0.5">Confirm everything before creating</p>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: "Orders", value: `${selectedGroups.length}` },
                    { label: "Total Distance", value: `${legs.reduce((acc, leg) => acc + parseFloat(leg.distance || 0), 0).toFixed(1)} km` },
                    { label: "Est. Weight", value: `~${Math.round(totalSelectedWeight).toLocaleString()} kg` },
                    { label: "Total Value", value: `₹${totalSelectedValue.toLocaleString()}` },
                  ].map((s) => (
                    <Card key={s.label} className="p-3 text-center">
                      <p className="text-xl font-bold">{s.value}</p>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                    </Card>
                  ))}
                </div>

                <Card className="p-4">
                  {[
                    { label: "Name", value: tripName || suggestedName },
                    { label: "Type", value: tripType === "hub_and_spoke" ? "Hub & Spoke" : "Direct" },
                    { label: "Priority", value: priority.charAt(0).toUpperCase() + priority.slice(1) },
                    { label: "Origin", value: origin },
                    { label: "Destination", value: selectedOrderDetails.map((o) => o.destination).join(", ") },
                    { label: "Est. Delivery", value: estimatedDelivery },
                    { label: "Partner", value: partners.find((p) => p.id === selectedPartnerId)?.name || "Not assigned" },
                    { label: "Vehicle", value: vehicles.find((v) => v.id === selectedVehicleId)?.registration || "To be assigned" },
                  ].map((row) => (
                    <div key={row.label} className="flex items-start text-sm">
                      <span className="text-muted-foreground w-28 flex-shrink-0">{row.label}</span>
                      <span className="font-medium">{row.value}</span>
                    </div>
                  ))}
                  {notes && (
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-muted-foreground w-28 flex-shrink-0">Notes</span>
                      <span>{notes}</span>
                    </div>
                  )}
                  <div className="pt-3 mt-3 border-t border-dashed space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Trip Assignments</p>
                    {trips.map(trip => (
                      <div key={trip.id} className="flex items-start gap-3 text-sm">
                        <span className="text-muted-foreground w-28 flex-shrink-0 font-medium flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${trip.color}`} />
                          {trip.label}
                        </span>
                        <span className="font-medium text-foreground">
                          {partners.find(p => p.id === trip.partnerId)?.name || "Not Assigned"}
                          {trip.vehicleId && ` · ${vehicles.find(v => v.id === trip.vehicleId)?.registration}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>

                <div>
                  <p className="text-sm font-medium mb-2">Orders</p>
                  <div className="space-y-1.5">
                    {selectedOrderDetails.map((o) => {
                      const grp = selectedGroups.find((g) => g?.orderId === o?.id);
                      const items = grp?.itemIds?.length === 0 ? o?.items : o?.items?.filter((i) => grp?.itemIds?.includes(i.id));
                      return (
                        <div key={o?.id} className="flex items-center gap-3 p-2.5 border rounded-lg text-sm">
                          <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-mono font-medium">{o?.order_number}</span>
                          <span className="flex-1 text-muted-foreground">{o?.customer_name}</span>
                          <span className="text-muted-foreground text-xs">{items?.length} items</span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="w-3 h-3" />{o?.destination}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {legs.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Route ({routeMode === "linear" ? "Linear" : routeMode === "round_trip" ? "Round Trip" : "Multi-Trip"} · {legs.length} leg{legs.length !== 1 ? "s" : ""})</p>
                    <RouteCanvas legs={legs} trips={trips} routeMode={routeMode} />
                    <div className="space-y-1.5">
                      {trips.map((trip) => {
                        const tripLegs = legs.filter((l) => trip.legIds.includes(l.id));
                        return (
                          <div key={trip.id}>
                            {trips.length > 1 && (
                              <div className="flex items-center gap-2 mb-1">
                                <div className={`w-2.5 h-2.5 rounded-full ${trip.color}`} />
                                <p className="text-xs font-medium">{trip.label}</p>
                                {trip.handoffPoint && <span className="text-xs text-muted-foreground">· Handoff: {trip.handoffPoint}</span>}
                              </div>
                            )}
                            {tripLegs.map((leg) => (
                              <div key={leg.id} className={`flex items-center gap-3 p-2.5 border rounded-lg text-sm ${leg.isReturn ? "bg-gray-50 border-dashed" : ""}`}>
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${trip.color}`}>{leg.sequence}</span>
                                <span className="text-muted-foreground text-xs flex-shrink-0">{LEG_TYPE_LABELS[leg.type]}</span>
                                <span className="font-medium truncate">{leg.from || origin}</span>
                                <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium truncate flex-1">{leg.to}</span>
                                {leg.carrier && <span className="text-xs text-muted-foreground">{leg.carrier}</span>}
                                <span className="text-xs text-muted-foreground flex-shrink-0">ETA: {leg.arrivalPlanned ? leg.arrivalPlanned.split("T")[0] : estimatedDelivery}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              )}

{/*           {step === 3 && (() => {
            // Determine the active trip to show in the UI
            const currentAssignTripId = activeAssignTripId || trips[0]?.id;
            const activeTrip = trips.find(t => t.id === currentAssignTripId);

            return (
              <div className="space-y-5">
                <div>
                  <h4 className="font-medium">Assign Delivery Partner & Vehicle</h4>
                  <p className="text-sm text-muted-foreground mt-0.5">Assign a 3PL, courier, or own-fleet vehicle for each individual trip</p>
                </div>

                {trips.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-2 border-b border-border">
                    {trips.map(trip => {
                      const isActive = currentAssignTripId === trip.id;
                      return (
                        <button
                          key={trip.id}
                          onClick={() => setActiveAssignTripId(trip.id)}
                          className={`px-4 py-2.5 rounded-t-lg border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                            isActive 
                              ? 'border-blue-500 text-blue-700 bg-blue-50/50' 
                              : 'border-transparent text-muted-foreground hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${trip.color}`} />
                            {trip.label}
                            {trip.partnerId && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {activeTrip && (
                  <div className="space-y-5 animate-in fade-in duration-300">
                    <div>
                      <p className="text-sm font-medium mb-2">Delivery Partner for <span className="text-blue-700">{activeTrip.label}</span></p>
                      <div className="grid grid-cols-2 gap-3">
                        {partners.filter((p) => p.status === "active").map((p) => {
                          const typeCfg = partnerTypeConfig[p.type];
                          const isSelected = activeTrip.partnerId === p.id;
                          
                          return (
                            <button 
                              key={p.id} 
                              onClick={() => { 
                                updateTrip(activeTrip.id, "partnerId", p.id); 
                                updateTrip(activeTrip.id, "vehicleId", ""); // Reset vehicle if partner changes
                              }} 
                              className={`text-left p-4 rounded-xl border-2 transition-colors ${isSelected ? "border-foreground bg-foreground/5" : "border-border hover:border-foreground/40"}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeCfg.color}`}>{typeCfg.label}</span>
                                  {isSelected && <CheckCircle className="w-4 h-4 text-green-500" />}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                  <span className="text-xs font-medium">{p.rating}</span>
                                </div>
                              </div>
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{p.contactName}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className={`text-xs font-bold ${p.onTimeRate >= 90 ? "text-green-600" : p.onTimeRate >= 80 ? "text-yellow-600" : "text-red-600"}`}>
                                  {p.onTimeRate}% on-time
                                </span>
                                <span className="text-xs text-muted-foreground">{p.activeTrips} active</span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {p.coverageZones.slice(0, 3).map((z) => (<span key={z} className="text-xs bg-muted px-1.5 py-0.5 rounded">{z}</span>))}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {activeTrip.partnerId && (
                      <div>
                        <p className="text-sm font-medium mb-2">
                          Vehicle <span className="text-muted-foreground font-normal">(optional — assign now or later)</span>
                        </p>
                        <div className="space-y-2">
                          {vehicles
                            .filter((v) => v.partnerId === activeTrip.partnerId || !v.partnerId)
                            .map((v) => {
                              const statusCfg = vehicleStatusConfig[v.status];
                              const isAvail = v.status === "available";
                              const isSelected = activeTrip.vehicleId === v.id;
                              
                              return (
                                <button 
                                  key={v.id} 
                                  disabled={!isAvail} 
                                  onClick={() => updateTrip(activeTrip.id, "vehicleId", isSelected ? "" : v.id)} 
                                  className={`w-full text-left flex items-center gap-4 p-3 rounded-lg border-2 transition-colors ${isSelected ? "border-foreground bg-foreground/5" : isAvail ? "border-border hover:border-foreground/40" : "border-border opacity-50 cursor-not-allowed"}`}
                                >
                                  <span className="text-xl flex-shrink-0">{vehicleTypeIcon[v.type]}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm font-medium">{v.registration}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusCfg.color}`}>{statusCfg.label}</span>
                                      {isSelected && <CheckCircle className="w-4 h-4 text-green-500" />}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                                      <span>{v.driver}</span>
                                      <span>·</span>
                                      <span>{v.driverPhone}</span>
                                      <span>·</span>
                                      <span>Cap: {v.capacity}</span>
                                      {v.currentLocation && <><span>·</span><span>📍 {v.currentLocation}</span></>}
                                    </div>
                                  </div>
                                  {v.assignedTrip && (<span className="text-xs text-blue-600 font-medium flex-shrink-0">{v.assignedTrip}</span>)}
                                </button>
                              );
                            })}
                          {vehicles.filter((v) => v.partnerId === activeTrip.partnerId || !v.partnerId).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-6">No vehicles linked to this partner</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()} */}

{/*             {step === 4 && (
              <div className="space-y-5">
                <div>
                  <h4 className="font-medium">Review & Create Trip(s)</h4>
                  <p className="text-sm text-muted-foreground mt-0.5">Confirm everything before creating</p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Orders", value: `${selectedGroups.length}` },
                    { label: "Est. Weight", value: `~${Math.round(totalSelectedWeight).toLocaleString()} kg` },
                    { label: "Total Value", value: `₹${totalSelectedValue.toLocaleString()}` },
                  ].map((s) => (
                    <Card key={s.label} className="p-3 text-center">
                      <p className="text-xl font-bold">{s.value}</p>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                    </Card>
                  ))}
                </div>

                <Card className="p-4">
                  {[
                    { label: "Name", value: tripName || suggestedName },
                    { label: "Type", value: tripType === "hub_and_spoke" ? "Hub & Spoke" : "Direct" },
                    { label: "Priority", value: priority.charAt(0).toUpperCase() + priority.slice(1) },
                    { label: "Origin", value: origin },
                    { label: "Destination", value: selectedOrderDetails.map((o) => o.destination).join(", ") },
                    { label: "Est. Delivery", value: estimatedDelivery },
                    { label: "Partner", value: partners.find((p) => p.id === selectedPartnerId)?.name || "Not assigned" },
                    { label: "Vehicle", value: vehicles.find((v) => v.id === selectedVehicleId)?.registration || "To be assigned" },
                  ].map((row) => (
                    <div key={row.label} className="flex items-start text-sm">
                      <span className="text-muted-foreground w-28 flex-shrink-0">{row.label}</span>
                      <span className="font-medium">{row.value}</span>
                    </div>
                  ))}
                  {notes && (
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-muted-foreground w-28 flex-shrink-0">Notes</span>
                      <span>{notes}</span>
                    </div>
                  )}
                  <div className="pt-3 mt-3 border-t border-dashed space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Trip Assignments</p>
                    {trips.map(trip => (
                      <div key={trip.id} className="flex items-start gap-3 text-sm">
                        <span className="text-muted-foreground w-28 flex-shrink-0 font-medium flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${trip.color}`} />
                          {trip.label}
                        </span>
                        <span className="font-medium text-foreground">
                          {partners.find(p => p.id === trip.partnerId)?.name || "Not Assigned"}
                          {trip.vehicleId && ` · ${vehicles.find(v => v.id === trip.vehicleId)?.registration}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>

                <div>
                  <p className="text-sm font-medium mb-2">Orders</p>
                  <div className="space-y-1.5">
                    {selectedOrderDetails.map((o) => {
                      const grp = selectedGroups.find((g) => g.orderId === o.id);
                      const items = grp?.itemIds?.length === 0 ? o.items : o.items.filter((i) => grp.itemIds.includes(i.id));
                      return (
                        <div key={o.id} className="flex items-center gap-3 p-2.5 border rounded-lg text-sm">
                          <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-mono font-medium">{o.order_number}</span>
                          <span className="flex-1 text-muted-foreground">{o.customer_name}</span>
                          <span className="text-muted-foreground text-xs">{items?.length} items</span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="w-3 h-3" />{o.destination}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {legs.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Route ({routeMode === "linear" ? "Linear" : routeMode === "round_trip" ? "Round Trip" : "Multi-Trip"} · {legs.length} leg{legs.length !== 1 ? "s" : ""})</p>
                    <RouteCanvas legs={legs} trips={trips} routeMode={routeMode} />
                    <div className="space-y-1.5">
                      {trips.map((trip) => {
                        const tripLegs = legs.filter((l) => trip.legIds.includes(l.id));
                        return (
                          <div key={trip.id}>
                            {trips.length > 1 && (
                              <div className="flex items-center gap-2 mb-1">
                                <div className={`w-2.5 h-2.5 rounded-full ${trip.color}`} />
                                <p className="text-xs font-medium">{trip.label}</p>
                                {trip.handoffPoint && <span className="text-xs text-muted-foreground">· Handoff: {trip.handoffPoint}</span>}
                              </div>
                            )}
                            {tripLegs.map((leg) => (
                              <div key={leg.id} className={`flex items-center gap-3 p-2.5 border rounded-lg text-sm ${leg.isReturn ? "bg-gray-50 border-dashed" : ""}`}>
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${trip.color}`}>{leg.sequence}</span>
                                <span className="text-muted-foreground text-xs flex-shrink-0">{LEG_TYPE_LABELS[leg.type]}</span>
                                <span className="font-medium truncate">{leg.from || origin}</span>
                                <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium truncate flex-1">{leg.to}</span>
                                {leg.carrier && <span className="text-xs text-muted-foreground">{leg.carrier}</span>}
                                <span className="text-xs text-muted-foreground flex-shrink-0">ETA: {leg.arrivalPlanned ? leg.arrivalPlanned.split("T")[0] : estimatedDelivery}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )} */}
          </div>
        </ScrollArea>

      <div className="p-5 border-t bg-muted/30 flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-2">
          {step > 1 && (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)}>Back</Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
          <div className="flex items-center gap-3">
          {step < 6 && (<p className="text-xs text-muted-foreground">Step {step} of 6</p>)}
          
          {step === 1 && (
              <Button onClick={() => setStep(2)} className="text-white bg-blue-600 hover:bg-blue-700">
              Next: Create Shipments <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
          )}
          {step === 2 && (
              <Button disabled={sessionShipments.length === 0} onClick={() => setStep(3)} className="text-white bg-blue-600 hover:bg-blue-700">
              Next: Priority <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
          )}
          {step === 3 && (
              <Button onClick={() => setStep(4)} className="text-white bg-blue-600 hover:bg-blue-700">
              Next: Route Builder <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
          )}
          {step === 4 && (
              <Button onClick={() => setStep(5)} className="text-white bg-blue-600 hover:bg-blue-700">
              Next: Assign Partner <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
          )}
          {step === 5 && (
              <Button disabled={!canProceedStep3} onClick={() => setStep(6)} className="text-white bg-blue-600 hover:bg-blue-700">
              Review Trip <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
          )}
          {step === 6 && (
              <Button onClick={handleCreateTrip} className="text-white bg-green-600 hover:bg-green-700 px-6">
              <CheckCircle className="w-4 h-4 mr-2" />
              Create Trip
              </Button>
          )}
          </div>
      </div>
      </div>
    </div>
  );
}

// ─── Main Screen ─────────────────────────────────────────────────────────────
export function DeliveryScreen() {
  const [trips, setTrips] = useState([/* mockTrips */]);
  const [partners,setPartners] = useState([/* mockPartners */]);
  const [vehicles,setVehicles] = useState([/* mockVehicles */]);
  const [fleets,setFleets] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [searchTrips, setSearchTrips] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

useEffect(() => {
  const token = decryptData(sessionStorage.getItem("token"));

  // Fetch Trips
  const fetchTrips = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/trips`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token || "",
          },
        }
      );

      const data = await response.json();

      console.log("Trips Response:", data);

      if (data.success) {
        setTrips(data.data || []);
      } else {
        console.error("Failed to fetch trips:", data);
      }
    } catch (error) {
      console.error("Error fetching trips:", error);
    }
  };

  // Fetch Delivery Partners
  const fetchPartners = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/get-all-delivery-partners`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token || "",
          },
        }
      );

      const data = await response.json();

      console.log("Partners Response:", data);

      if (data.success) {
        setPartners(data.data?.partners || []);
        setFleets(data.data?.fleets || []);
        setVehicles(data.data?.vehicle_types || []);
      } else {
        console.error("Failed to fetch partners:", data);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  fetchTrips();
  fetchPartners();
}, []);

  const handleTripCreated = (newTrip) => {
    setTrips((prev) => [newTrip, ...prev]);
  };

/*   const handleStatusChange = (id, status) => {
    setTrips((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    const label = tripStatusConfig[status].label;
    toast.success(`Trip ${id} updated to "${label}"`);
  }; */

  const handleStatusChange = async (id, status) => {
    console.log("Updating trip status:", id, status);
    const token = decryptData(sessionStorage.getItem("token"));
    try {
      const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-trip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify({ trip_id: id, status: status })
      });
      const result = await res.json();
      if (result.success) {
        setTrips((prev) => prev.map((s) => (s._id === id || s.id === id ? { ...s, status } : s)));
        toast.success(`Trip updated to ${status}`);
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const filteredTrips = trips
    .filter((s) => {
      const matchSearch =
        searchTrips === "" ||
        s.id.toLowerCase().includes(searchTrips.toLowerCase()) ||
        s.name.toLowerCase().includes(searchTrips.toLowerCase()) ||
        s.orders.some((o) => o.customer_name.toLowerCase().includes(searchTrips.toLowerCase()));
      const matchStatus = statusFilter === "all" || s.status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const p = { high: 0, medium: 1, low: 2 };
      const urgent = ["delayed", "draft", "assigned"];
      const aU = urgent.includes(a.status) ? 0 : 1;
      const bU = urgent.includes(b.status) ? 0 : 1;
      if (aU !== bU) return aU - bU;
      return p[a.priority] - p[b.priority];
    });

  // ── Hook Pagination for Trips matching OrdersManager pattern ──
  const {
    currentItems: paginatedTrips,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    //resetToFirstPage,
  } = usePagination(filteredTrips, 4);

  const handleSearchChange = (e) => {
    setSearchTrips(e.target.value);
    //resetToFirstPage();
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    //resetToFirstPage();
  };

  const actionTrips = trips.filter((s) => ["draft", "assigned", "delayed"].includes(s.status));




  return (
    <div className="space-y-6">
      <Tabs defaultValue="trips">
        <div className="flex flex-row justify-between mr-10">
          <TabsList>
            <TabsTrigger
              value="trips"
              className="rounded-md px-4 data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm"
            >
              <Route className="w-4 h-4 mr-1.5" />
              Trips
              {actionTrips.length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs px-1.5">
                  {actionTrips.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="fleet"
              className="rounded-md px-4 data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm"
            >
              <Truck className="w-4 h-4 mr-1.5" />
              Fleet & Partners
            </TabsTrigger>
            <TabsTrigger
              value="tracking"
              className="rounded-md px-4 data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm"
            >
              <MapPin className="w-4 h-4 mr-1.5" />
              Order Tracking
            </TabsTrigger>
          </TabsList>
          <Button
            className="bg-[#f7525a] hover:bg-[#f7525a] text-white flex-shrink-0"
            onClick={() => setShowCreateWizard(true)}
            title=""
            data-tooltip="Plan Trips"
            style={{ '--tooltip-bg': 'black' }}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Trips Tab */}
        <TabsContent value="trips" className="space-y-4 mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={5}
            showInfo={true}
            hideControls={true}
            itemName="trips"
          />

          <div className="flex items-center gap-3 flex-wrap justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search Trips..."
                  value={searchTrips}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="relative status-dropdown">
                {["all", "draft", "assigned", "dispatched", "in_transit", "at_hub", "out_for_delivery", "delivered", "delayed"].map((s) => (
                  <button
                    key={s}
                    onClick={() => handleStatusFilterChange(s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      statusFilter === s
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background text-muted-foreground border-border hover:border-foreground"
                    }`}
                  >
                    {s === "all" ? "All" : tripStatusConfig[s].label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2 pb-8">
            {paginatedTrips.length === 0 ? (
              <div className="text-center rounded-lg border border-gray-100 py-16 flex flex-col items-center justify-center text-gray-500 mt-4">
                <Truck className="w-8 h-8 mb-2 text-gray-400" />
                <p className="text-sm font-medium">No Trips Found</p>
              </div>
            ) : (
              paginatedTrips.map((s) => (
                console.log("Rendering TripRow for trip:", s) ||
                <TripRow
                  key={s._id}
                  trip={s}
                  partners={partners}
                  onSelect={setSelectedTrip}
                  onQuickDispatch={(_id) => handleStatusChange(_id, "dispatched")}
                />
              ))
            )}
          </div>

          <div className="bg-white border-t sticky bottom-0 left-0 w-full z-20 px-4 py-2 shadow-sm">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={5}
              onPageChange={handlePageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              showInfo={false}
              itemName="Trips"
            />
          </div>
        </TabsContent>

        {/* Fleet & Partners Tab */}
        <TabsContent value="fleet" className="mt-4">
          <ScrollArea className="h-[calc(100vh-260px)] pr-4">
            <FleetView fleets={fleets} partners={partners} vehicles={vehicles} />
          </ScrollArea>
        </TabsContent>

        {/* Order Tracking Tab */}
        <TabsContent value="tracking" className="mt-4">
          <OrderTrackingView trips={trips} />
        </TabsContent>
      </Tabs>

      {/* Detail panel */}
      {selectedTrip && (
        <TripDetailPanel
          trip={selectedTrip}
          fleets={fleets}
          partners={partners}
          vehicles={vehicles}
          onClose={() => setSelectedTrip(null)}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Create Trip Wizard */}
      {showCreateWizard && (
        <CreateTripWizard
          onClose={() => setShowCreateWizard(false)}
          onCreated={handleTripCreated}
          fleets={fleets}
          partners={partners}
          vehicles={vehicles}
        />
      )}
    </div>
  );
}